const LANGUAGE_KEY = 'anforderungsai:language';

const createLanguageLabels = () => ({
  de: 'DE',
  en: 'EN',
  fr: 'FR',
  es: 'ES',
});

export const AVAILABLE_LANGUAGES = ['de', 'en', 'fr', 'es'];

const normalizeLanguage = (language) => (AVAILABLE_LANGUAGES.includes(language) ? language : 'en');

const IMPRINT_DETAILS = Object.freeze({
  name: 'Steffen Everding',
  street: 'Getrud-Bäumer-Str. 6',
  postalCodeCity: '80637 München',
  country: 'Deutschland',
  email: 'steffen.everding@gmail.com',
  taxId: 'DE292381657',
});

const detectBrowserLanguage = () => {
  if (typeof navigator === 'undefined') return null;
  const candidate = navigator.language?.slice(0, 2)?.toLowerCase();
  return candidate ? normalizeLanguage(candidate) : null;
};

const loadStoredLanguage = () => {
  try {
    const stored = localStorage.getItem(LANGUAGE_KEY);
    if (stored) {
      return normalizeLanguage(stored);
    }
  } catch (error) {
    console.warn('Language storage unavailable', error);
  }
  return detectBrowserLanguage() ?? 'en';
};

export const getStoredLanguage = () => {
  try {
    const stored = localStorage.getItem(LANGUAGE_KEY);
    return stored ? normalizeLanguage(stored) : null;
  } catch (error) {
    console.warn('Language storage unavailable', error);
    return null;
  }
};

const saveLanguage = (language) => {
  try {
    localStorage.setItem(LANGUAGE_KEY, language);
  } catch (error) {
    console.warn('Language storage failed', error);
  }
};

const LANG_CONTENT = {
  de: {
    common: {
      loading: 'Lade Daten…',
      noData: 'Noch keine Daten vorhanden.',
      error: 'Daten konnten nicht geladen werden.',
      authRequired: 'Bitte melde dich an, um diese Daten zu sehen.',
      download: {
        button: 'Download',
        title: 'Download',
        subtitle: 'Format auswählen und Export starten.',
        formatLabel: 'Format',
        templateLabel: 'Vorlage',
        standardOption: 'Standard Export',
        manageTemplates: 'Vorlagen verwalten',
        noTemplates: 'Keine Vorlagen verfügbar.',
        cancel: 'Abbrechen',
        confirm: 'Download starten',
        loading: 'Download läuft…',
      },
    },
    nav: {
      home: 'Start',
      dashboard: 'Dashboard',
      requirements: 'Ereignisse',
      customersProjects: 'Kunden / Projekte',
      upload: 'Upload',
      newRequirements: 'Neues Ereignis',
      jobs: 'Gespräche',
      live: 'Live',
      appFactory: 'App Factory',
      profile: 'Account',
      admin: 'Admin',
      imprint: 'Impressum',
      login: 'Login',
      register: 'Registrieren',
    },
    sidebar: {
      tagline: 'Klare Ereignisse für Teams',
      navigationLabel: 'Navigation',
      accessLabel: 'Access',
      themeLabel: 'Theme',
      languageLabel: 'Sprache',
      loggedInLabel: 'Angemeldet als',
      logout: 'Logout',
      logoutDemo: 'Demo beenden',
      menuOpen: 'Menü öffnen',
      menuClose: 'Menü schließen',
      languages: createLanguageLabels(),
    },
    releaseNotes: {
      title: 'Was ist neu?',
      subtitle: 'Änderungen seit deinem letzten Login.',
      confirmButton: 'Verstanden',
      ackInProgress: 'Bestätigung wird gespeichert…',
      ackError: 'Bestätigung fehlgeschlagen.',
      fallbackTitle: 'Release',
    },
    home: {
      heroEyebrow: 'Requirements-Management für Produkt- und Projektteams',
      heroTitle: 'Von Gespräch zu klaren Ereignisse in Minuten.',
      heroDescription: `AnforderungsAI extrahiert Ereignisse aus unterschiedlichsten Quellen – etwa aus Live-Interviews, Workshops und Videocalls sowie aus Dokumenten, Transkripten, Audio- und Videodateien.
Die Inhalte werden automatisch strukturiert, kontextualisiert und priorisiert und stehen anschließend in verschiedenen Formaten zur Weiterverarbeitung und zum Export bereit.`,
      heroMore:
        'Die Pipeline transkribiert Audio- und Videodateien, erkennt relevante Aussagen und verbindet sie mit Metadaten. So entsteht ein nachvollziehbarer Anforderungskatalog, der Teams bei Review, Priorisierung und Übergabe unterstützt.',
      heroToggleMore: 'Produkt-Details',
      heroToggleLess: 'Details ausblenden',
      ctas: {
        primary: 'Demo anfragen',
        secondary: 'Demomodus',
        pricing: 'Preise ansehen',
        register: 'Demo anfragen',
      },
      trust: [
        'DSGVO-orientiert',
        'EU-Hosting',
        'CSV, Markdown & JSON',
        'Live oder Upload',
      ],
      featuresTitle: 'Was AnforderungsAI liefert',
      featuresSubtitle: 'Für Teams, die aus Gesprächen verwertbare Ereignisse machen wollen.',
      features: [
        {
          title: 'Live- und Datei-Workflows',
          description: 'Starte eine Live-Session oder lade Audio, Video oder Transcript hoch.',
        },
        {
          title: 'Extraktion mit Kontext',
          description: 'Jede Anforderung bleibt mit Quelle, Begründung und Tags nachvollziehbar.',
        },
        {
          title: 'Struktur & Priorisierung',
          description: 'Typen, Status und Prioritäten sind sauber sortiert und filterbar.',
        },
        {
          title: 'Exportfähige Ergebnisse',
          description: 'CSV, Markdown oder JSON per Template – bereit für Doku und Backlog.',
        },
        {
          title: 'Teilen im Team',
          description: 'Ergebnisse lassen sich per Link teilen und gemeinsam prüfen.',
        },
        {
          title: 'Datenschutz in der EU',
          description: 'Verarbeitung und Speicherung sind für europäische Ereignisse ausgelegt.',
        },
      ],
      stepsTitle: 'So läuft es ab',
      stepsSubtitle: 'In drei klaren Schritten von der Quelle zum verwertbaren Output.',
      steps: [
        {
          title: 'Quelle wählen',
          description: 'Interview, Workshop oder bestehendes Dokument auswählen.',
        },
        {
          title: 'Analyse läuft',
          description: 'Transkription und Extraktion erzeugen Ereignisse mit Kontext.',
        },
        {
          title: 'Ergebnisse übernehmen',
          description: 'Prüfen, priorisieren und als Export weitergeben.',
        },
      ],
      demo: {
        title: 'Sofort testen',
        description:
          'Lade eine Text-, CSV- oder JSON-Datei. Wir zeigen dir eine erkannte Anforderung als Vorschau. Für die vollständige Analyse brauchst du einen Login.',
        button: 'Datei prüfen',
        note: 'Die Demo wird lokal im Browser verarbeitet. Es wird nichts hochgeladen.',
        error: 'Beim Lesen der Datei ist ein Fehler aufgetreten.',
        noRequirement: 'Keine Anforderung erkannt.',
        downloadLabel: 'Beispieldatei herunterladen',
        fileLabel: 'Datei auswählen',
        formatsHint: 'Formate: TXT, MD, CSV, JSON.',
        customerLabel: 'Kunde',
        projectLabel: 'Projekt',
        snippetLabel: 'Auszug',
        successTitle: 'Beispiel-Ergebnis',
        successNote: 'Für vollständige Ergebnisse bitte anmelden.',
        selectFileWarning: 'Bitte wähle eine Datei aus.',
        loading: 'Analysiere Datei …',
      },
      demoFile: {
        customer: 'Nordachse Mobility',
        project: 'Ladehub EU',
        snippet: 'Der Ladehub muss modulare Stationen unterstützen und binnen 2 Minuten auf Peak-Verbrauch reagieren können.',
      },
      pricingTitle: 'Preise',
      pricingSubtitle: 'Transparente Abrechnung, jederzeit anpassbar. Zahlungen laufen über Stripe.',
      pricingNote: 'Für Pilot- oder Enterprise-Bedarf erstellen wir individuelle Pakete.',
      pricingDetails: {
        quota: {
          monthly: 'Uploads pro Monat inklusive',
          per_job: 'Flexible Abrechnung pro verarbeitetem Upload',
          unlimited: 'Unbegrenzte Uploads, priorisierte Verarbeitung',
          default: 'Flexible Nutzung je nach Bedarf',
        },
        billingRequired: 'Zahlungsdaten erforderlich',
        free: 'Kostenlos starten',
        actionLabel: 'Tarif wählen',
        unitPriceLabel: '€3 / Nutzung',
      },
      testimonialsTitle: 'Stimmen aus Projekten',
      testimonialsSubtitle:
        'Produkt-, Projekt- und Beratungsteams nutzen AnforderungsAI, um Ereignisse konsistent zu erfassen und schneller zu übergeben.',
      testimonials: [
        {
          quote: 'Wir erhalten Ereignisse inklusive Quelle und sparen uns die Nacharbeit im Review.',
          author: 'Produktmanagerin, B2B SaaS',
        },
        {
          quote: 'Die Exportvorlagen liefern uns ein sauberes Format für Doku und Backlog.',
          author: 'Scrum Master, Digitalagentur',
        },
        {
          quote: 'Gerade in frühen Phasen bekommen wir schnell Struktur in Stakeholder-Feedback.',
          author: 'IT-Projektleitung, Mittelstand',
        },
      ],
      contactButton: 'Demo anfragen',
      demoButton: 'Demomodus',
      faqTitle: 'Häufige Fragen',
      faqSubtitle: 'Kurz beantwortet, damit die Entscheidung leichter fällt.',
      faq: [
        {
          question: 'Welche Formate werden unterstützt?',
          answer: 'Audio/Video (MP3, MP4, WAV, QTA) sowie Transkripte (TXT, MD, JSON, CSV, VTT, SRT).',
        },
        {
          question: 'Was passiert mit meinen Daten?',
          answer: 'Die Demo-Datei wird lokal im Browser verarbeitet. Für die vollständige Analyse wird die Datei hochgeladen und im Workspace verarbeitet.',
        },
        {
          question: 'Wie sehen die Ergebnisse aus?',
          answer: 'Strukturierte Ereignisse mit Quelle, Tags und optionalen User Stories. Export als CSV, Markdown oder JSON.',
        },
        {
          question: 'Wie schnell erhalte ich Ergebnisse?',
          answer: 'In der Regel innerhalb weniger Minuten, abhängig von Länge und Format der Quelle.',
        },
      ],
      ctaTitle: 'Klarheit in eure Ereignisse bringen',
      ctaSubtitle: 'Vereinbare eine Demo oder teste die Extraktion mit deiner eigenen Datei.',
      ctaPrimary: 'Demo anfragen',
      ctaSecondary: 'Demomodus',
    },
    upload: {
      title: 'Datei hochladen',
      formatsHint: 'Akzeptierte Formate: MP3, MP4, WAV, QTA.',
      description:
        'Unterstützte Formate: MP3, MP4, WAV, QTA. Nach dem Upload startet Transcribe automatisch und erstellt die Extraktion.',
      fileLabel: 'Audio- oder Video-Datei',
      engineLabel: 'Transkriptions-Engine',
      engineDescription: 'Wähle die Engine für die Spracherkennung.',
      engines: {
        mistral: 'Mistral (Voxtral)',
        aws: 'AWS Transcribe',
      },
      customerLabel: 'Kunde (optional)',
      projectLabel: 'Projekt (optional)',
      meetingLabel: 'Meeting-Datum (optional)',
      customerPlaceholder: 'Kundenkennung',
      projectPlaceholder: 'Projektname',
      domainLabel: 'Themenbereich',
      domainOptions: [
        { value: 'enterprise-software', label: 'Enterprise-Software' },
        { value: 'bau', label: 'Bau' },
        { value: 'maschinenbau', label: 'Maschinenbau' },
        { value: 'produktentwicklung', label: 'Produktentwicklung' },
        { value: 'sonstiges', label: 'Sonstiges' },
      ],
      domainDefault: 'enterprise-software',
      button: 'Upload starten',
      selectFileWarning: 'Bitte wähle eine Datei aus.',
      creatingLink: 'Erzeuge Upload-Link…',
      uploading: 'Lade Datei hoch…',
      uploadDone: 'Upload abgeschlossen.',
      uploadError: 'Upload fehlgeschlagen.',
      planNote: 'Aktueller Tarif:',
      planNoteCta: 'Tarif verwalten',
      listTitle: 'Aktuelle Uploads',
      progressLabel: 'Fortschritt',
      successTitle: 'Upload erfolgreich',
      conversationLabel: 'Conversation ID',
      viewJobs: 'Jobs ansehen',
      copyId: 'ID kopieren',
      copySuccess: 'Kopiert!',
      copyError: 'Konnte nicht kopieren',
      successNote: 'Die Verarbeitung läuft nun im Hintergrund. Schau in der Job-Übersicht vorbei.',
      statuses: {
        running: 'In Verarbeitung',
        success: 'Bereit für Export',
        error: 'Fehler',
      },
      transcript: {
        title: 'Transcript hochladen',
        description: 'Wenn du bereits ein Text- oder JSON-Transcript hast, kannst du es direkt hochladen und ohne erneute Spracherkennung auswerten.',
        fileLabel: 'Transcript-Datei',
        formatsHint: 'Unterstützt: TXT, MD, JSON, CSV, VTT, SRT, PDF, DOCX.',
        button: 'Transcript verarbeiten',
        successTitle: 'Transcript übernommen',
        successNote: 'Die Extraktion startet sofort auf Basis deines Transkripts.',
        status: {
          readingPdf: 'PDF wird gelesen…',
          pdfEmpty: 'PDF enthält keinen lesbaren Text.',
          readingDocx: 'Word-Datei wird gelesen…',
          docxEmpty: 'Word-Datei enthält keinen lesbaren Text.',
          docUnsupported: 'Legacy .doc wird nicht unterstützt. Bitte als .docx speichern.',
          finalizing: 'Starte Auswertung…',
          uploadError: 'Transcript-Upload fehlgeschlagen.',
        },
      },
    },
    newRequirements: {
      title: 'Neue Ereignisse',
      subtitle: 'Starte live oder lade Dateien/Transkripte hoch, um Ereignisse zu erstellen.',
      uploadTitle: 'Audio / Video hochladen',
      live: {
        badge: 'Live',
        title: 'Live-Transkription',
        description: 'Erfasse Gespräche in Echtzeit und extrahiere Ereignisse direkt aus dem Gespräch.',
        note: 'Audio aus Mikrofon oder System wird live übertragen.',
      },
    },
    requirementsList: {
      title: 'Ereignisse',
      subtitle: 'Alle Ereignisse über Kunden, Projekte und Zeiträume.',
      summary: {
        total: 'Gesamt',
      },
      actions: {
        refresh: 'Aktualisieren',
        exportCsv: 'CSV Export',
        exportMd: 'Markdown Export',
        exportJson: 'JSON Export',
        exportXlsx: 'Excel Export',
        exportDocx: 'Word Export',
        download: 'Download',
      },
      filters: {
        search: 'Suche',
        searchPlaceholder: 'Text, Tags, Kategorien...',
        customer: 'Kunde',
        customerPlaceholder: 'Kundenkennung',
        project: 'Projekt',
        projectPlaceholder: 'Projektname',
        from: 'Von',
        to: 'Bis',
        status: 'Status',
        statusAll: 'Alle Status',
        customerAll: 'Alle Kunden',
        projectAll: 'Alle Projekte',
        none: 'Ohne',
        priority: 'Priorität',
        priorityAll: 'Alle Prioritäten',
        type: 'Art',
        typeAll: 'Alle Arten',
        reset: 'Filter zurücksetzen',
      },
      status: {
        NEW: 'Neu',
        OPEN: 'Offen',
        BACKLOG: 'Backlog',
        IN_PROGRESS: 'In Arbeit',
        DONE: 'Fertig',
        ARCHIVED: 'Archiviert',
      },
      priorities: {
        must: 'Muss',
        should: 'Soll',
        could: 'Kann',
        wont: "Won't",
      },
      types: {
        aufgabe: 'Aufgabe',
        termin: 'Termin',
        entscheidung: 'Entscheidung',
        anforderung: 'Anforderung',
        frage: 'Frage',
        risiko: 'Risiko',
        annahme: 'Annahme',
        deferred: 'Vertagung',
        sonstiges: 'Sonstiges',
        blocker: 'Blocker',
        dependency: 'Abhängigkeit',
        functional: 'Funktional',
        nonfunctional: 'Nicht-funktional',
        constraint: 'Einschränkung',
        risk: 'Risiko',
        assumption: 'Annahme',
        open_question: 'Offene Frage',
      },
      table: {
        text: 'Ereignis',
        customer: 'Kunde/Projekt',
        date: 'Datum',
        type: 'Art',
        priority: 'Prio',
        status: 'Status',
        actions: 'Aktion',
        open: 'Öffnen',
        copyLabel: 'Kopieren',
        copySuccess: 'Inhalt kopiert.',
        copyError: 'Kopieren fehlgeschlagen.',
      },
      empty: 'Keine Ereignisse gefunden.',
      errors: {
        load: 'Ereignisse konnten nicht geladen werden.',
        save: 'Status konnte nicht gespeichert werden.',
      },
      toast: {
        statusSaved: 'Status aktualisiert.',
      },
    },
    customersProjects: {
      title: 'Kunden / Projekte',
      description: 'Wähle einen Kunden und ein Projekt, um eine automatische Zusammenfassung und Q&A zu sehen.',
      panes: {
        customers: 'Kunden',
        projects: 'Projekte',
      },
      labels: {
        projects: 'Projekte',
        conversations: 'Gespräche',
        lastUpdate: 'Aktualisiert',
        whatItDoes: 'Was macht das Projekt?',
        whatItIsAbout: 'Worum geht es?',
        nextSteps: 'Was steht als nächstes an?',
        highlights: 'Highlights',
        fallback: 'Fallback',
        sourceStats: 'Quellen: {{used}} / {{total}} Gespräche ausgewertet',
      },
      states: {
        indexLoading: 'Kunden und Projekte werden geladen…',
        emptyTitle: 'Noch keine Daten',
        emptyDescription: 'Bitte starte einen Upload oder eine Live-Session.',
        noProjects: 'Für diesen Kunden wurden noch keine Projekte gefunden.',
        selectProject: 'Bitte wähle ein Projekt aus.',
        selectProjectForQa: 'Q&A wird aktiv, sobald ein Projekt ausgewählt ist.',
        summaryLoading: 'Zusammenfassung wird erstellt…',
        summaryChecking: 'Prüfe Updates…',
        noSummary: 'Keine Zusammenfassung verfügbar.',
      },
      errors: {
        indexTitle: 'Kunden/Projekte konnten nicht geladen werden.',
        indexDescription: 'Bitte versuche es erneut.',
        summaryTitle: 'Zusammenfassung konnte nicht geladen werden.',
        summaryDescription: 'Bitte versuche es erneut.',
      },
      actions: {
        refresh: 'Aktualisieren',
        retry: 'Erneut laden',
        upload: 'Upload starten',
      },
      qaTitle: 'Q&A zum Projekt',
      qaDescription: 'Frage gezielt zu diesem Kunden/Projekt. Antworten enthalten Quellen.',
    },
    jobs: {
      title: 'Gespräche',
      description: 'Die Liste aktualisiert sich automatisch alle paar Sekunden.',
      refresh: 'Aktualisieren',
      searchLabel: 'Jobs suchen',
      searchPlaceholder: 'Suche nach Kunde, Projekt, Status oder Inhalt…',
      searchLoading: 'Suche lädt…',
      columns: {
        customer: 'Kunde',
        project: 'Projekt',
        meeting: 'Meeting',
        status: 'Status',
        source: 'Quelle',
        engine: 'Engine',
        updated: 'Erstellt',
        actions: '',
        delete: 'Löschen',
      },
      sourceLabel: 'Upload · Mistral',
      openButton: 'Öffnen',
      failedLabel: 'Fehlgeschlagen',
      statusUnknown: 'Unbekannt',
      deleteLabel: 'Löschen',
      deleteConfirm: 'Diesen Job wirklich löschen?',
      deleteError: 'Job konnte nicht gelöscht werden.',
      statusLabels: {
        APP_FACTORY_ACTIVE: 'App Factory aktiv',
        LIVE_ACTIVE: 'Live aktiv',
        LIVE: 'Live aktiv',
      },
      noMatches: 'Kein Job passt zu deiner Suche.',
      empty: 'Noch keine Uploads vorhanden.',
      loadError: 'Jobs konnten nicht geladen werden.',
      steps: {
        UPLOADED: 'Upload',
        TRANSCRIBING: 'Transkription',
        EXTRACTING: 'Extraktion',
        READY: 'Fertig',
      },
      engineLabels: {
        none: '—',
      },
      sourceLabels: {
        live: 'Live',
        upload: 'Upload',
        transcript: 'Transcript',
        appFactory: 'App Factory',
        unknown: 'Unbekannt',
      },
    },
    dashboard: {
      welcome: {
        heading: 'Hallo {{name}}',
        defaultName: 'du',
        planLabel: 'Dein Tarif:',
        planFallback: 'Unbekannt',
        priceFallback: '—',
        subtitle: 'Übersicht über Kunden, Projekte und Jobs.',
        onboardingNote: 'Tipp: Öffne einen Job, um Themenbereiche zu filtern und Ereignisse direkt mit Änderungslog zu bearbeiten.',
        actions: {
          live: 'Live starten',
          upload: 'Upload aus Datei',
          jobs: 'Jobliste',
          profile: 'Profil & Einstellungen',
        },
      },
      panels: {
        title: 'Deine wichtigsten Panels',
        subtitle: 'Richte dir den Arbeitsbereich so ein, wie du ihn brauchst.',
        adminOnly: 'ADMIN ONLY',
        manageOpen: 'Panels verwalten',
        manageClose: 'Panel-Auswahl schließen',
        pinnedEmpty: 'Pinne Panels, um sie hier zu sehen.',
        libraryEmpty: 'Du hast bereits alle Panels angepinnt.',
        pin: 'Anpinnen',
        unpin: 'Lösen',
        entries: {
          liveControl: {
            badge: 'Realtime',
            title: 'Live-Sitzung',
            description: 'Starte Ereignisse direkt aus dem Gespräch und teile den Raum live.',
            activeLabel: 'Aktive Sessions',
            startButton: 'Session starten',
            shareButton: 'Live teilen',
            note: 'Alle Teilnehmenden sehen dieselbe Single Source of Truth.',
          },
          uploadFlow: {
            badge: 'Audio',
            title: 'Upload-Pipeline',
            description: 'Visuelle Schritte für deine Audio-Uploads.',
            steps: ['Upload & Validierung', 'Transkription', 'Anforderungsextraktion', 'Export im Wunschformat'],
            note: 'Wenn eine Stufe hängt, kannst du sie gezielt neu anstoßen.',
          },
          exportHandoff: {
            badge: 'Formate',
            title: 'Export & Handoff',
            description: 'Liefer Ergebnisse in genau dem Artefakt, das dein Kunde erwartet.',
            formats: ['User Stories & Jira-Import', 'Lasten-/Pflichtenheft', 'Maschinenbau & DIN-konforme Vorlagen'],
            primaryButton: 'Format wählen',
            secondaryButton: 'Download',
          },
          pilotProgram: {
            badge: 'Pilotkunden',
            title: 'Pilot & Kampagnen',
            description: 'Halte Pilotkunden und Social-Media-Leads synchron.',
            activeLabel: 'Aktive Pilotkunden',
            note: 'Feedback landet direkt bei AnforderungsAI – ideal für schnelle Iterationen.',
          },
          deviceReadiness: {
            badge: 'Geräte',
            title: 'Device Readiness',
            description: 'V1: Desktop, Tablets, Phones. V2: Apple Watch, weitere Devices & Teams-Plugin.',
            devices: ['Desktop', 'Tablet', 'Handy'],
            note: 'Plane Watch & Integrationen früh mit ein, um Live-Sichtbarkeit zu halten.',
          },
        },
      },
      insights: {
        title: 'Live-Insights',
        subtitle: 'Status über Kunden, Projekte und laufende Sessions.',
        planFallback: 'Tarif',
        cards: {
          total: { label: 'Jobs insgesamt', subline: 'inkl. Live & Uploads' },
          running: { label: 'Laufend', subline: 'mit aktiver Transkription' },
          completed: { label: 'Fertig', subline: 'bereit für Export' },
          customers: { label: 'Kunden & Projekte', subline: 'Unique Kunden / Projekte' },
        },
      },
      portfolio: {
        title: 'Kunden & Projekte',
        subtitle: 'Schneller Überblick über aktuelle Kundenbeziehungen und aktive Projekte.',
        customersTitle: 'Kunden',
        projectsTitle: 'Projekte',
        customerLine: '{{projects}} Projekte · {{jobs}} Jobs',
        projectNamesLabel: 'Projekte: {{names}}',
        requirementsTotalLabel: 'Ereignisse gesamt: {{count}}',
        peopleLabel: 'Personen: {{names}}',
        projectStatusLabel: 'Status',
        projectStatusActive: 'aktiv',
        projectStatusInactive: 'inaktiv',
        jobLabel: 'Meeting {{date}}',
        requirementsCountLabel: '{{count}} Ereignisse',
        loadingLabel: '…',
        updatedLabel: 'Aktualisiert {{date}}',
        unknownCustomer: 'Ohne Kunde',
        empty: 'Noch keine Kunden- oder Projektdaten vorhanden.',
      },
      jobs: {
        title: 'Letzte Jobs',
        countLoading: 'Lade Daten…',
        countEmpty: 'Noch keine Jobs',
        countLabel: 'Zeige {{visible}} von {{total}} Jobs',
        searchLabel: 'Suchen',
        searchPlaceholder: 'Kunde, Projekt oder Status',
        filters: {
          all: 'Alle',
          running: 'Laufend',
          completed: 'Fertig',
          error: 'Fehler',
        },
        table: {
          customer: 'Kunde',
          project: 'Projekt',
          meeting: 'Meeting',
          status: 'Status',
          updated: 'Erstellt',
          actions: '',
          open: 'Öffnen',
          empty: 'Noch keine Jobs vorhanden. Starte eine Live-Session oder lade Audio hoch.',
          noMatches: 'Kein Job passt zu deinem Filter.',
        },
        loading: 'Lade Daten…',
        loadError: 'Jobs konnten nicht geladen werden.',
        loadFailedLabel: 'Fehler beim Laden',
      },
      statusLabels: {
        RUNNING: 'Läuft',
        PROCESSING: 'Verarbeitung',
        TRANSCRIBING: 'Transkription',
        EXTRACTING: 'Analyse',
        COMPLETED: 'Fertig',
        READY: 'Bereit',
        SUCCESS: 'Fertig',
        DONE: 'Fertig',
        FAILED: 'Fehler',
        ERROR: 'Fehler',
        CANCELED: 'Abgebrochen',
        PENDING: 'Wartend',
      },
    },
    plans: {
      title: 'Tarife & Abrechnung',
      pillFallback: 'Unbekannter Tarif',
      billing: {
        noInfo: 'Keine Informationen verfügbar.',
        missing: 'Noch keine Zahlungsdaten hinterlegt.',
        active: 'Abrechnung aktiv.',
        statusLabel: 'Status',
        subscriptionLabel: 'Abo',
      },
      card: {
        current: 'Aktueller Tarif',
        checkout: 'Buchen',
        activate: 'Tarif aktivieren',
      },
      messages: {
        checkoutSuccess: 'Stripe Checkout abgeschlossen. Tarif wird in Kürze aktualisiert.',
        checkoutCancel: 'Bezahlvorgang abgebrochen.',
        updateSuccess: 'Tarif aktualisiert.',
        updateError: 'Aktualisierung fehlgeschlagen.',
        checkoutCreateError: 'Stripe Checkout konnte nicht erstellt werden.',
        checkoutFailed: 'Stripe Checkout fehlgeschlagen.',
      },
    },
    profile: {
      title: 'Account',
      tabs: {
        account: 'Konto Informationen',
        personal: 'Persönliche Daten',
        plan: 'Tarif',
        export: 'Export Vorlagen',
        billing: 'Abrechnungen',
        releaseNotes: 'Release Notes',
      },
      sectionTitles: {
        account: 'Kontoinformationen',
        plan: 'Aktueller Tarif',
        profile: 'Persönliche Daten',
        password: 'Passwort ändern',
      },
      labels: {
        email: 'E-Mail-Adresse',
        billing: 'Aktuelle Abrechnung:',
        requiredNote: '* Pflichtfelder',
        firstName: 'Vorname *',
        lastName: 'Nachname *',
        company: 'Firma',
        phone: 'Telefonnummer *',
        vatNumber: 'Umsatzsteuer-ID (optional)',
        street: 'Straße & Nr. *',
        postalCode: 'PLZ *',
        city: 'Stadt *',
        country: 'Land *',
        profileButton: 'Profil speichern',
        currentPassword: 'Aktuelles Passwort',
        newPassword: 'Neues Passwort',
        confirmPassword: 'Neues Passwort bestätigen',
        passwordButton: 'Passwort aktualisieren',
        deleteButton: 'Account löschen',
      },
      releaseNotes: {
        title: 'Release Notes',
        subtitle: 'Alle Änderungen im Überblick.',
        empty: 'Noch keine Release Notes vorhanden.',
        loading: 'Lade Release Notes…',
        loadError: 'Release Notes konnten nicht geladen werden.',
        fallbackTitle: 'Release',
      },
      plan: {
        unknown: 'Unbekannter Tarif.',
        togglePicker: 'Tarif wechseln',
        noAlternatives: 'Keine weiteren Tarife verfügbar.',
        alternativesTitle: 'Verfügbare Tarife',
        switchButton: 'Wechseln',
        currentBadge: 'Aktueller Tarif',
        pendingLabel: 'Tarifwechsel auf "{{plan}}" ausstehend.',
        notices: {
          pending: {
            title: 'Tarifwechsel ausstehend',
            description: 'Der Wechsel auf "{{plan}}" ist noch nicht abgeschlossen. Bitte beende den Zahlungsprozess, um den Tarif freizuschalten.',
            action: 'Bezahlvorgang fortsetzen',
          },
          payment: {
            title: 'Zahlungsmethode erforderlich',
            description: 'Für "{{plan}}" ist eine aktive Zahlungsmethode erforderlich. Bitte hinterlege deine Zahlungsdaten, um Uploads freizuschalten.',
            action: 'Zahlungsmethode hinterlegen',
          },
        },
        billingFallback: 'Karte',
        billingMissing: 'Keine Zahlungsdaten hinterlegt.',
      },
      statuses: {
        planAlreadyActive: 'Dieser Tarif ist bereits aktiv.',
        unknownPlan: 'Unbekannter Tarif.',
        openingCheckout: 'Öffne Stripe Checkout…',
        updatingPlan: 'Aktualisiere Tarif…',
        redirectingStripe: 'Weiterleitung zu Stripe…',
        planUpdated: 'Tarif aktualisiert.',
        planFailed: 'Tarifwechsel fehlgeschlagen.',
        profileSaving: 'Speichere Änderungen…',
        profileSaved: 'Profil gespeichert.',
        profileError: 'Profil konnte nicht gespeichert werden.',
        passwordMissingCurrent: 'Aktuelles Passwort ist erforderlich.',
        passwordTooShort: 'Neues Passwort muss mindestens 6 Zeichen enthalten.',
        passwordMismatch: 'Neues Passwort und Bestätigung stimmen nicht überein.',
        passwordUpdating: 'Aktualisiere Passwort…',
        passwordUpdated: 'Passwort aktualisiert.',
        passwordError: 'Passwort konnte nicht aktualisiert werden.',
        deleteConfirm: 'Möchtest du deinen Account wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.',
        deleteInProgress: 'Lösche Account…',
        deleteSuccess: 'Account gelöscht. Du wirst abgemeldet.',
        deleteFailed: 'Account konnte nicht gelöscht werden.',
      },
    },
    auth: {
      titles: {
        login: 'Anmeldung',
        register: 'Registrierung',
      },
      sections: {
        profile: 'Persönliche Angaben',
      },
      descriptions: {
        login: 'Melde dich mit deiner E-Mail an, um auf deine Uploads zuzugreifen.',
        register:
          'Lege ein neues Konto an. Passwörter werden verschlüsselt gespeichert. Du kannst später jederzeit einen kostenpflichtigen Tarif buchen.',
      },
      requiredNote: '* Pflichtfelder',
      labels: {
        email: 'E-Mail *',
        password: 'Passwort *',
        firstName: 'Vorname *',
        lastName: 'Nachname *',
        company: 'Firma (optional)',
        phone: 'Telefonnummer *',
        vatNumber: 'Umsatzsteuer-ID (optional)',
        street: 'Straße & Nr. *',
        postalCode: 'PLZ *',
        city: 'Stadt *',
        country: 'Land *',
      },
      placeholders: {
        company: 'Unternehmen',
        phone: '+49 170 123456',
        vatNumber: 'DE123456789',
        street: 'Straße 1',
        postalCode: '12345',
        city: 'Stadt',
        country: 'Deutschland',
      },
      planSelector: {
        title: 'Tarif wählen',
        subtitle: 'Wähle den Tarif, der am besten passt. Du kannst ihn später ändern.',
        selectButton: 'Tarif wählen',
        selectedButton: 'Ausgewählt',
        currentLabel: 'Aktueller Tarif: {{plan}}',
        unknownPlan: 'Unbekannt',
      },
      buttons: {
        submit: {
          login: 'Login',
          register: 'Registrieren',
        },
      },
    toggle: {
      login: {
        question: 'Noch kein Konto?',
        action: 'Konto erstellen',
      },
      register: {
        question: 'Bereits registriert?',
        action: 'Zum Login',
      },
    },
    forgotLink: 'Passwort vergessen?',
    forgot: {
      title: 'Passwort vergessen',
      subtitle: 'Gib deine E-Mail-Adresse ein. Wir senden dir einen Reset-Link.',
      emailLabel: 'E-Mail-Adresse',
      submit: 'Reset-Link senden',
      sending: 'Sende Reset-Link…',
      sent: 'Wenn ein Konto existiert, wurde eine E-Mail zum Zurücksetzen gesendet.',
      error: 'Anfrage fehlgeschlagen.',
      backToLogin: 'Zurück zum Login',
    },
    reset: {
      title: 'Passwort zurücksetzen',
      subtitle: 'Setze ein neues Passwort für dein Konto.',
      emailLabel: 'E-Mail-Adresse',
      passwordLabel: 'Neues Passwort',
      confirmLabel: 'Neues Passwort bestätigen',
      submit: 'Passwort speichern',
      saving: 'Passwort wird gespeichert…',
      success: 'Passwort aktualisiert. Du kannst dich jetzt anmelden.',
      error: 'Passwort konnte nicht aktualisiert werden.',
      missingToken: 'Reset-Link ist ungültig oder fehlt.',
      backToRequest: 'Neuen Link anfordern',
      backToLogin: 'Zurück zum Login',
      passwordTooShort: 'Neues Passwort muss mindestens 6 Zeichen enthalten.',
      passwordMismatch: 'Passwörter stimmen nicht überein.',
    },
    statuses: {
      checkingCredentials: 'Prüfe Zugangsdaten…',
      creatingAccount: 'Erstelle Konto…',
        successRedirect: 'Erfolgreich. Weiterleitung…',
        genericError: 'Aktion fehlgeschlagen.',
        redirectingCheckout: 'Weiterleitung zu Zahlung…',
        checkoutMissingUrl: 'Checkout URL fehlt in Antwort.',
        checkoutErrorPrefix: 'Checkout-Fehler:',
      },
    },
    admin: {
      title: 'Admin Konsole',
      description: 'Übersicht aller registrierten Konten. Änderungen greifen sofort (lokaler Demo-Store).',
      toggle: { open: 'Neuer Benutzer', close: 'Abbrechen' },
      loading: 'Lade Benutzer…',
      empty: 'Noch keine Benutzer angelegt.',
      addUser: { title: 'Benutzer anlegen', submit: 'Benutzer anlegen' },
      form: {
        firstName: 'Vorname',
        lastName: 'Nachname',
        email: 'E-Mail',
        password: 'Passwort',
        company: 'Firma',
        phone: 'Telefonnummer',
        vat: 'Umsatzsteuer-ID (optional)',
        street: 'Straße & Nr.',
        postalCode: 'PLZ',
        city: 'Stadt',
        country: 'Land',
        plan: 'Tarif',
        role: 'Rolle',
        uiVersion: 'UI-Version',
        newPassword: 'Neues Passwort',
        confirmPassword: 'Passwort bestätigen',
      },
      placeholders: {
        company: 'Unternehmen',
        phone: '+49 170 123456',
        vat: 'DE123456789',
        street: 'Straße 1',
        postalCode: '12345',
        city: 'Stadt',
        country: 'Deutschland',
      },
      table: {
        headers: {
          profile: 'Profil',
          registered: 'Registriert',
          plan: 'Tarif',
          role: 'Rolle',
          uiVersion: 'UI-Version',
          stripe: 'Stripe',
          jobsCurrentMonth: 'Jobs (Monat)',
          jobsTotal: 'Jobs (Gesamt)',
          jobs: 'Jobs',
          jobsCurrentMonthSub: 'pro Monat',
          jobsTotalSub: 'gesamt',
          actions: 'Aktionen',
        },
        profileFallback: '—',
        editButton: 'Bearbeiten',
      },
      stripe: { none: '—', open: 'öffne' },
      jobStats: {
        title: 'Jobs pro Monat',
        columns: { month: 'Monat', count: 'Jobs' },
        empty: 'Keine Jobs erfasst.',
      },
      plan: { unknown: 'Unbekannter Tarif' },
      roles: { admin: 'Admin', user: 'User' },
      uiVersion: {
        optionLabel: 'Version {{version}}',
        badgeLabel: 'V{{version}}',
        invalid: 'Ungültige UI-Version.',
      },
      actions: { close: 'Schließen', save: 'Speichern', cancel: 'Abbrechen', delete: 'Account löschen' },
      modal: { title: 'Benutzer bearbeiten' },
      statuses: {
        createRequired: 'E-Mail und Passwort sind erforderlich.',
        createSuccess: 'Benutzer {{email}} angelegt.',
        createFailed: 'Anlegen fehlgeschlagen.',
        passwordTooShort: 'Neues Passwort muss mindestens 6 Zeichen enthalten.',
        passwordMismatch: 'Neues Passwort und Bestätigung stimmen nicht überein.',
        updateSaving: 'Speichere Änderungen…',
        updateSuccess: 'Änderungen für {{email}} gespeichert.',
        updateFailed: 'Speichern fehlgeschlagen.',
        deleteConfirm: 'Wollen Sie wirklich den Account löschen?',
        deleteInProgress: 'Archiviere Account…',
        deleteSuccess: 'Account {{email}} archiviert.',
        deleteFailed: 'Archivieren fehlgeschlagen.',
        loadFailed: 'Benutzer konnten nicht geladen werden.',
      },
    },
    live: {
      title: 'Live-Transkription',
      buttons: {
        start: 'Session starten',
        stop: 'Session beenden',
        settingsOpen: 'Einstellungen',
        settingsClose: 'Einstellungen verbergen',
        transcriptShow: 'Live-Transkript anzeigen',
        transcriptHide: 'Live-Transkript verbergen',
        share: 'Link teilen',
      },
      fields: {
        customerLabel: 'Kunde (optional)',
        customerPlaceholder: 'Kunde',
        projectLabel: 'Projekt (optional)',
        projectPlaceholder: 'Projekt',
        meetingLabel: 'Meeting-Datum (optional)',
      },
      domainLabel: 'Themenbereich',
      domainOptions: [
        { value: 'enterprise-software', label: 'Enterprise-Software' },
        { value: 'bau', label: 'Bau' },
        { value: 'maschinenbau', label: 'Maschinenbau' },
        { value: 'produktentwicklung', label: 'Produktentwicklung' },
        { value: 'sonstiges', label: 'Sonstiges' },
      ],
      domainDefault: 'enterprise-software',
      info: {
        engineLabel: 'Engine',
        languageLabel: 'Sprache',
        languageUnknown: 'Noch nicht erkannt.',
        languageManualSuffix: ' (manuell)',
        statusReady: 'Bereit. Warte auf Start.',
        extractionWarning:
          'Ereignisse konnten nicht vollständig validiert werden. Es werden zuletzt bekannte Ergebnisse angezeigt.',
        footer:
          'Live-Session starten: Mikrofon und Systemaudio werden parallel gestreamt und in Echtzeit transkribiert. Bitte Browserzugriff auf beide Audioquellen zulassen.',
      },
      audio: {
        systemLabel: 'System',
        micLabel: 'Mikrofon',
        sourceSystem: 'Systemaudio aktiv',
        sourceMic: 'Mikrofon aktiv',
      },
      audioModes: [
        { code: 'mix', label: 'System + Mikrofon', description: 'Systemaudio und Mikrofon parallel aufnehmen' },
      ],
      requirements: {
        title: 'Erkannte Ereignisse',
        empty: 'Noch keine Ereignisse extrahiert.',
        fallbackType: 'Sonstiges',
        shareMissing: 'Noch keine Ereignisse vorhanden.',
        shareCopied: 'Link kopiert!',
        shareCreateError: 'Link konnte nicht erstellt werden.',
        shareClipboardError: 'Link kopieren fehlgeschlagen. <a href="{{url}}" target="_blank">Link öffnen</a>',
      },
      transcript: {
        empty: 'Noch kein Audio empfangen.',
      },
      statuses: {
        settingsLockedLanguage: 'Aktive Session – Sprachauswahl gesperrt.',
        settingsLockedEngine: 'Aktive Session – Engine kann nicht gewechselt werden.',
        settingsLockedAudio: 'Aktive Session – Audioquelle gesperrt.',
        settingsLockedDomain: 'Aktive Session – Themenbereich gesperrt.',
        connecting: 'Verbinde…',
        systemAudioUnsupported: 'Systemaudio nicht verfügbar – Aufnahme nur mit Mikrofon.',
        chunkBuffered: 'Audiofragment unvollständig – warte auf weitere Daten…',
        chunkSkipped: 'Audiochunk übersprungen – war unvollständig.',
        sessionStreaming: 'Session aktiv – Streaming läuft.',
        chunkSendFailed: 'Übertragung fehlgeschlagen. Versuche erneut…',
        waitingAudio: 'Session aktiv – warte auf erste Audio-Daten…',
        waitingAudioMic: 'Session aktiv – warte auf erste Audio-Daten (nur Mikrofon).',
        stopError: 'Fehler beim Beenden der Session.',
        startError: 'Session konnte nicht gestartet werden.',
      },
      appFactory: {
        title: 'App Factory Leitfaden',
        description: 'Steuere strukturierte Kundeninterviews direkt im Live-Modus.',
        sessionSelectLabel: 'Session auswählen',
        sessionSelectPlaceholder: 'Session wählen…',
        sessionEmpty: 'Noch keine App-Factory-Sessions vorhanden.',
      buttons: {
        refresh: 'Aktualisieren',
        newSession: 'Neue Session',
        analyze: 'Fortschritt analysieren',
        sync: 'Ereignisse übertragen',
        open: 'Session öffnen',
      },
      sessionLabel: 'Session',
      customerLabelShort: 'Kunde',
      projectLabelShort: 'Projekt',
      autoSyncLabel: 'Neue Ereignisse automatisch übertragen',
        requirementsTitle: 'Live-Ereignisse',
        requirementsHint: 'Wähle eine Session, um Ereignisse zu synchronisieren.',
        requirementsSummary: '{{count}} Ereignisse bereit.',
        guidelineEmpty: 'Kein Leitfaden geladen.',
        status: {
          selectSession: 'Bitte Session auswählen.',
          loadingSessions: 'Lade Sessions…',
          loadingSession: 'Leitfaden wird geladen…',
          syncEmpty: 'Noch keine Ereignisse vorhanden.',
          syncing: 'Ereignisse werden übertragen…',
          syncSuccess: 'Ereignisse synchronisiert.',
          analyzing: 'Analyse läuft…',
          updatedAt: 'Aktualisiert um {{time}}',
        },
        errors: {
          load: 'Sessions konnten nicht geladen werden.',
          create: 'Session konnte nicht erstellt werden.',
          analyze: 'Fortschritt konnte nicht analysiert werden.',
          sync: 'Ereignisse konnten nicht übertragen werden.',
          session: 'Session konnte nicht geladen werden.',
          init: 'App Factory konnte nicht initialisiert werden.',
        },
        prompts: {
          customer: 'Optional: Kunden-ID für die Session eingeben',
          project: 'Optional: Projekt-ID für die Session eingeben',
        },
        creating: 'Session wird erstellt…',
      },
    },
    liveShare: {
      title: 'Freigegebene Ereignisse',
      loadError: 'Link konnte nicht gelesen werden. Bitte fordere ihn erneut an.',
      sessionLabel: 'Session vom {{date}}',
      sharedBy: {
        prefix: 'Geteilt von',
        unknown: 'unbekannt',
        companySeparator: ' – ',
      },
      buttons: {
        csv: 'CSV herunterladen',
        md: 'Markdown herunterladen',
      },
      empty: 'Es wurden keine Ereignisse übertragen.',
      note: 'Hinweis: Diese Ansicht zeigt eine Momentaufnahme der Live-Session. Echtzeit-Updates benötigen das zukünftige Backend.',
      card: {
        fallbackType: 'Sonstiges',
      },
      download: {
        csvHeaders: {
          type: 'Typ',
          text: 'Text',
        },
        markdown: {
          title: 'Erkannte Ereignisse',
          sessionLabel: 'Session',
        },
      },
    },
    conversation: {
      title: 'Conversation',
      loading: 'Lade Ergebnisse…',
      backToJobs: 'Zurück zu Jobs',
      backLabel: 'Zurück',
      header: {
        status: 'Status',
        conversationId: 'Conversation ID',
        customer: 'Kunde',
        project: 'Projekt',
        meeting: 'Meeting',
        created: 'Erstellt',
        updated: 'Aktualisiert',
      },
      progress: {
        label: 'Status',
        phases: {
          uploaded: 'Hochgeladen',
          processing: 'In Verarbeitung',
          transcribing: 'Transkription läuft',
          extracting: 'Ereignisse werden extrahiert',
          live: 'Live-Sitzung aktiv',
          finalizing: 'Finalisierung',
          finalized: 'Abgeschlossen',
          failed: 'Fehler',
          unknown: 'Unbekannt',
        },
      },
      delete: {
        button: 'Job löschen',
        confirm: 'Diesen Job wirklich löschen? Alle Ereignisse, Transkripte und Dateien werden entfernt.',
        inProgress: 'Lösche…',
        success: 'Job wurde gelöscht.',
        error: 'Job konnte nicht gelöscht werden.',
      },
      metaEdit: {
        button: 'Bearbeiten',
        save: 'Speichern',
        cancel: 'Abbrechen',
        inProgress: 'Speichere…',
        error: 'Aktualisierung fehlgeschlagen.',
      },
      filters: {
        topics: 'Themenbereiche',
        topicsAll: 'Alle Themen',
        noTopics: 'Keine Themen vorhanden.',
        contentLabel: 'Anzeige',
        contentAll: 'Alles',
        priorityLabel: 'Priorität',
        priorityAll: 'Alle Prioritäten',
        categoryLabel: 'Kategorie',
        categoryAll: 'Alle Kategorien',
        searchLabel: 'Suche',
        searchPlaceholder: 'Text, ID oder Stichwort',
        clear: 'Filter zurücksetzen',
        countLabel: '{{count}} Ergebnisse',
        noResults: 'Kein Eintrag passt zu deinem Filter.',
        emptyAll: 'Noch keine Ereignisse oder User Stories.',
        priorityOptions: {
          high: 'Hoch',
          medium: 'Mittel',
          low: 'Niedrig',
          must: 'Must',
          should: 'Should',
          could: 'Could',
          wont: "Won't",
        },
        topicLabels: {
          functional: 'Funktional',
          non_functional: 'Nicht funktional',
          business: 'Business',
          knowledge_management: 'Wissensmanagement',
          system_integration: 'Systemintegration',
          lifecycle_tracking: 'Lifecycle-Tracking',
          onboarding_enablement: 'Onboarding Enablement',
          global_enablement: 'Global Enablement',
          data_governance: 'Data Governance',
          analytics_automation: 'Analytics Automation',
          service_quality: 'Servicequalität',
          other: 'Sonstiges',
        },
      },
      sections: {
        requirements: 'Ereignisse',
        userStories: 'User Stories',
        deferredDecisions: 'Vertagte Entscheidungen',
      },
      summary: {
        title: 'Executive Summary',
        download: 'Summary herunterladen',
        downloadDescription: 'Executive Summary (Markdown)',
        collapse: 'Einklappen',
        expand: 'Ausklappen',
        overview: 'Kurzüberblick',
        keyRequirements: 'Kernereignisse',
        openDecisions: 'Offene Entscheidungen',
        risks: 'Risiken',
        nextSteps: 'Nächste Schritte',
        downloadSuccess: 'Executive Summary Download gestartet.',
        downloadError: 'Executive Summary konnte nicht geladen werden.',
      },
      exports: {
        csv: 'CSV Export',
        md: 'Markdown Export',
      },
      requirements: {
        empty: 'Noch keine Ereignisse verfügbar.',
        rationale: 'Begründung',
        source: 'Quelle',
        speaker: 'Sprecher',
        tags: 'Tags',
      },
      edit: {
        editButton: 'Bearbeiten',
        copyButton: 'Kopieren',
        copySuccess: 'Inhalt kopiert.',
        copyError: 'Kopieren fehlgeschlagen.',
        saveButton: 'Speichern',
        cancelButton: 'Abbrechen',
        priorityUnset: 'Ohne Priorität',
        fields: {
          text: 'Text',
          story: 'User Story',
          role: 'Rolle',
          goal: 'Ziel',
          benefit: 'Nutzen',
          acceptance: 'Akzeptanzkriterien',
          category: 'Kategorie',
          priority: 'Priorität',
          topics: 'Themenbereiche',
        },
        topicsPlaceholder: 'z.B. Sicherheit, Reporting',
      },
      userStories: {
        empty: 'Noch keine User Stories.',
        role: 'Rolle',
        goal: 'Ziel',
        benefit: 'Nutzen',
        acceptance: 'Akzeptanzkriterien',
        priorityFallback: 'Medium',
      },
      log: {
        title: 'Änderungslog',
        empty: 'Noch keine Änderungen.',
        note: 'Änderungen werden in der Datenbank gespeichert.',
        emptyValue: '—',
      },
    },
    qaChat: {
      title: 'Q&A-Chat mit Quellen',
      description: 'Stelle Fragen zu Ereignisse und Transkripten. Antworten enthalten nachvollziehbare Belege.',
      requirementsTitle: 'Q&A über alle Ereignisse',
      requirementsDescription: 'Aktuelle Filter werden als Kontext genutzt (Kunde, Projekt, Zeitraum).',
      conversationTitle: 'Q&A zur Conversation',
      conversationDescription: 'Frage gezielt zu diesem Gespräch mit Quellen inkl. Sprecher und Zeit.',
      roles: {
        user: 'Du',
        assistant: 'Assistent',
      },
      state: {
        loading: 'Chat wird geladen…',
        empty: 'Noch keine Nachrichten. Stelle deine erste Frage.',
      },
      input: {
        placeholder: 'Beispiel: Welche Farbe soll das Haus sein?',
      },
      actions: {
        send: 'Senden',
        newThread: 'Neuer Thread',
        deleteThread: 'Löschen',
        includeGeneralAnswer: '+ Allgemeinwissen (Test)',
        searchGlobal: 'Global suchen',
      },
      labels: {
        generalAnswer: 'Allgemeinwissen (Test)',
      },
      feedback: {
        threadDeleted: 'Thread gelöscht.',
      },
      citations: {
        title: 'Quellen',
        unknownSpeaker: 'Unbekannter Sprecher',
        unknownCustomer: 'Unbekannter Kunde',
        unknownProject: 'Unbekanntes Projekt',
      },
      errors: {
        default: 'Anfrage fehlgeschlagen.',
        send: 'Nachricht konnte nicht gesendet werden.',
        loadThread: 'Thread konnte nicht geladen werden.',
        createThread: 'Thread konnte nicht erstellt werden.',
        deleteThread: 'Thread konnte nicht gelöscht werden.',
        loadThreads: 'Threads konnten nicht geladen werden.',
      },
    },
    imprint: {
      title: 'Impressum',
      intro: 'Dieses Impressum gilt für das Online-Angebot von AnforderungsAI unter https://anforderungsai.de.',
      tabs: {
        imprint: 'Impressum',
        agb: 'AGBs',
        privacy: 'Datenschutz',
      },
      provider: {
        heading: 'Anbieter',
        nameLabel: 'Name',
        name: IMPRINT_DETAILS.name,
        addressLabel: 'Anschrift',
        addressLines: [IMPRINT_DETAILS.street, IMPRINT_DETAILS.postalCodeCity, IMPRINT_DETAILS.country],
        registerLabel: 'Registereintrag',
        registerNote: 'Kein Handelsregistereintrag vorhanden.',
      },
      contact: {
        heading: 'Kontakt',
        emailLabel: 'E-Mail',
        email: IMPRINT_DETAILS.email,
        phoneLabel: 'Telefon',
        phone: 'Nicht veröffentlicht – bitte per E-Mail anfragen.',
      },
      vat: {
        heading: 'Umsatzsteuer-ID',
        text: `USt-ID gemäß § 27a UStG: ${IMPRINT_DETAILS.taxId}`,
      },
      supervision: {
        heading: 'Aufsichtsbehörde',
        text: 'Es besteht keine besondere behördliche Aufsichtspflicht.',
      },
      profession: {
        heading: 'Berufsrechtliche Angaben',
        text: 'Keine reglementierten Berufe; besondere Berufsordnungen sind nicht einschlägig.',
      },
      dispute: {
        heading: 'Verbraucherschlichtung / EU-Streitschlichtung',
        text: 'Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:',
        platformLabel: 'https://ec.europa.eu/consumers/odr',
        participation: 'Wir sind nicht verpflichtet und nicht bereit, an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.',
      },
      editorial: {
        heading: 'Verantwortlich i.S.d. § 18 Abs. 2 MStV',
        text: `${IMPRINT_DETAILS.name}, gleiche Anschrift.`,
      },
      notes: {
        lastUpdated: 'Stand: Januar 2025',
      },
    },
  },
  en: {
    common: {
      loading: 'Loading data…',
      noData: 'No data yet.',
      error: 'Could not load data.',
      authRequired: 'Please sign in to view this data.',
      download: {
        button: 'Download',
        title: 'Download',
        subtitle: 'Choose a format and start the export.',
        formatLabel: 'Format',
        templateLabel: 'Template',
        standardOption: 'Standard export',
        manageTemplates: 'Manage templates',
        noTemplates: 'No templates available.',
        cancel: 'Cancel',
        confirm: 'Start download',
        loading: 'Downloading…',
      },
    },
    nav: {
      home: 'Home',
      dashboard: 'Dashboard',
      requirements: 'Requirements',
      customersProjects: 'Customers / Projects',
      upload: 'Upload',
      newRequirements: 'New Event',
      jobs: 'Jobs',
      live: 'Live',
      appFactory: 'App Factory',
      profile: 'Account',
      admin: 'Admin',
      imprint: 'Imprint',
      login: 'Login',
      register: 'Register',
    },
    sidebar: {
      tagline: 'Next-gen requirements cockpit',
      navigationLabel: 'Navigation',
      accessLabel: 'Access',
      themeLabel: 'Theme',
      languageLabel: 'Language',
      loggedInLabel: 'Signed in as',
      logout: 'Logout',
      logoutDemo: 'End demo',
      menuOpen: 'Open menu',
      menuClose: 'Close menu',
      languages: createLanguageLabels(),
    },
    releaseNotes: {
      title: "What's new?",
      subtitle: 'Changes since your last login.',
      confirmButton: 'Got it',
      ackInProgress: 'Saving confirmation…',
      ackError: 'Confirmation failed.',
      fallbackTitle: 'Release',
    },
    home: {
      heroEyebrow: 'Requirements management for product and project teams',
      heroTitle: 'From conversation to clear requirements in minutes.',
      heroDescription: `AnforderungsAI extracts requirements from a wide range of sources — including live interviews, workshops and video calls, as well as documents, transcripts, audio and video files.
The content is automatically structured, contextualized, and prioritized, then made available in multiple formats for further processing and export.`,
      heroMore:
        'The pipeline transcribes audio and video, identifies relevant statements and links them to metadata. The result is a traceable requirements catalogue that supports review, prioritization and handoff.',
      heroToggleMore: 'Product details',
      heroToggleLess: 'Hide details',
      ctas: {
        primary: 'Request demo',
        secondary: 'View demo',
        pricing: 'See pricing',
        register: 'Request demo',
      },
      trust: [
        'GDPR-oriented',
        'EU hosting',
        'CSV, Markdown & JSON',
        'Live or upload',
      ],
      featuresTitle: 'What AnforderungsAI delivers',
      featuresSubtitle: 'For teams that want to turn conversations into usable requirements.',
      features: [
        {
          title: 'Live and file workflows',
          description: 'Start a live session or upload audio, video or a transcript.',
        },
        {
          title: 'Extraction with context',
          description: 'Every requirement stays traceable with source, rationale and tags.',
        },
        {
          title: 'Structure & prioritization',
          description: 'Types, status and priorities are cleanly sorted and filterable.',
        },
        {
          title: 'Export-ready results',
          description: 'CSV, Markdown or JSON via templates — ready for docs and backlog.',
        },
        {
          title: 'Share with the team',
          description: 'Results can be shared by link and reviewed together.',
        },
        {
          title: 'Data protection in the EU',
          description: 'Processing and storage are designed for European requirements.',
        },
      ],
      stepsTitle: 'How it works',
      stepsSubtitle: 'In three clear steps from source to usable output.',
      steps: [
        {
          title: 'Choose a source',
          description: 'Select interview, workshop or existing document.',
        },
        {
          title: 'Analysis runs',
          description: 'Transcription and extraction generate contextual requirements.',
        },
        {
          title: 'Hand off results',
          description: 'Review, prioritize and export for handoff.',
        },
      ],
      demo: {
        title: 'Try it now',
        description:
          'Upload a text, CSV or JSON file. We show one detected requirement as a preview. For the full analysis you need a login.',
        button: 'Check file',
        note: 'The demo runs locally in your browser. Nothing is uploaded.',
        error: 'An error occurred while reading the file.',
        noRequirement: 'No requirement detected.',
        downloadLabel: 'Download sample file',
        fileLabel: 'Choose file',
        formatsHint: 'Formats: TXT, MD, CSV, JSON.',
        customerLabel: 'Customer',
        projectLabel: 'Project',
        snippetLabel: 'Excerpt',
        successTitle: 'Sample result',
        successNote: 'For full results please log in.',
        selectFileWarning: 'Please select a file.',
        loading: 'Analyzing file …',
      },
      demoFile: {
        customer: 'Nordachse Mobility',
        project: 'Charging hub EU',
        snippet: 'The charging hub must support modular stations and react to peak load within 2 minutes.',
      },
      pricingTitle: 'Pricing',
      pricingSubtitle: 'Transparent billing, adjustable at any time. Payments via Stripe.',
      pricingNote: 'For pilot or enterprise needs we offer custom packages.',
      pricingDetails: {
        quota: {
          monthly: 'Uploads per month included',
          per_job: 'Flexible billing per processed upload',
          unlimited: 'Unlimited uploads, priority processing',
          default: 'Flexible usage based on your needs',
        },
        billingRequired: 'Payment details required',
        free: 'Start for free',
        actionLabel: 'Select plan',
        unitPriceLabel: '€3 / Nutzung',
      },
      testimonialsTitle: 'Voices from projects',
      testimonialsSubtitle:
        'Product, project and consulting teams use AnforderungsAI to capture requirements consistently and hand them off faster.',
      testimonials: [
        {
          quote: 'We get requirements including source and save rework in reviews.',
          author: 'Product manager, B2B SaaS',
        },
        {
          quote: 'The export templates give us a clean format for docs and backlog.',
          author: 'Scrum master, digital agency',
        },
        {
          quote: 'Especially early on, we quickly gain structure in stakeholder feedback.',
          author: 'IT project lead, mid-sized company',
        },
      ],
      contactButton: 'Request demo',
      demoButton: 'View demo',
      faqTitle: 'Frequently asked questions',
      faqSubtitle: 'Short answers to make the decision easier.',
      faq: [
        {
          question: 'Which formats are supported?',
          answer: 'Audio/video (MP3, MP4, WAV, QTA) and transcripts (TXT, MD, JSON, CSV, VTT, SRT).',
        },
        {
          question: 'What happens to my data?',
          answer: 'The demo file is processed locally in the browser. For full analysis the file is uploaded and processed in your workspace.',
        },
        {
          question: 'What do the results look like?',
          answer: 'Structured requirements with source, tags and optional user stories. Export as CSV, Markdown or JSON.',
        },
        {
          question: 'How fast do I get results?',
          answer: 'Usually within a few minutes, depending on length and format of the source.',
        },
      ],
      ctaTitle: 'Bring clarity to your requirements',
      ctaSubtitle: 'Book a demo or test extraction with your own file.',
      ctaPrimary: 'Request demo',
      ctaSecondary: 'View demo',
    },
    upload: {
      title: 'Upload & processing',
      formatsHint: 'Accepted formats: MP3, MP4, WAV, QTA.',
      description: 'Supported formats: MP3, MP4, WAV, QTA. After upload, Transcribe starts automatically.',
      fileLabel: 'Audio or video file',
      engineLabel: 'Transcription engine',
      engineDescription: 'Choose the engine for speech recognition.',
      engines: {
        mistral: 'Mistral (Voxtral)',
        aws: 'AWS Transcribe',
      },
      customerLabel: 'Customer (optional)',
      projectLabel: 'Project (optional)',
      meetingLabel: 'Meeting date (optional)',
      customerPlaceholder: 'Customer ID',
      projectPlaceholder: 'Project name',
      domainLabel: 'Domain',
      domainOptions: [
        { value: 'enterprise-software', label: 'Enterprise software' },
        { value: 'bau', label: 'Construction' },
        { value: 'maschinenbau', label: 'Mechanical engineering' },
        { value: 'produktentwicklung', label: 'Product development' },
        { value: 'sonstiges', label: 'Other' },
      ],
      domainDefault: 'enterprise-software',
      button: 'Start upload',
      selectFileWarning: 'Please select a file.',
      creatingLink: 'Generating upload link…',
      uploading: 'Uploading file…',
      uploadDone: 'Upload finished.',
      uploadError: 'Upload failed.',
      planNote: 'Current plan:',
      planNoteCta: 'Manage plan',
      listTitle: 'Recent uploads',
      progressLabel: 'Progress',
      successTitle: 'Upload successful',
      conversationLabel: 'Conversation ID',
      viewJobs: 'View jobs',
      copyId: 'Copy ID',
      copySuccess: 'Copied!',
      copyError: 'Could not copy',
      successNote: 'Processing continues in the background. Check the Jobs view for updates.',
      statuses: {
        running: 'In progress',
        success: 'Ready for export',
        error: 'Error',
      },
      transcript: {
        title: 'Upload transcript',
        description: 'Already have a text or JSON transcript? Upload it and skip the speech-recognition step.',
        fileLabel: 'Transcript file',
        formatsHint: 'Supported: TXT, MD, JSON, CSV, VTT, SRT, PDF, DOCX.',
        button: 'Process transcript',
        successTitle: 'Transcript received',
        successNote: 'Extraction kicks off immediately based on your transcript.',
        status: {
          readingPdf: 'Reading PDF…',
          pdfEmpty: 'PDF does not contain readable text.',
          readingDocx: 'Reading Word document…',
          docxEmpty: 'Word document does not contain readable text.',
          docUnsupported: 'Legacy .doc is not supported. Please save as .docx.',
          finalizing: 'Starting extraction…',
          uploadError: 'Transcript upload failed.',
        },
      },
    },
    newRequirements: {
      title: 'New Requirements',
      subtitle: 'Start live or upload files/transcripts to create new requirements.',
      uploadTitle: 'Upload audio / video',
      live: {
        badge: 'Live',
        title: 'Live transcription',
        description: 'Capture conversations in real time and extract requirements right away.',
        note: 'Audio from mic or system is streamed live.',
      },
    },
    requirementsList: {
      title: 'Events',
      subtitle: 'All events across customers, projects, and time ranges.',
      summary: {
        total: 'Total',
      },
      actions: {
        refresh: 'Refresh',
        exportCsv: 'CSV export',
        exportMd: 'Markdown export',
        exportJson: 'JSON export',
        exportXlsx: 'Excel export',
        exportDocx: 'Word export',
        download: 'Download',
      },
      filters: {
        search: 'Search',
        searchPlaceholder: 'Text, tags, categories...',
        customer: 'Customer',
        customerPlaceholder: 'Customer ID',
        project: 'Project',
        projectPlaceholder: 'Project name',
        from: 'From',
        to: 'To',
        status: 'Status',
        statusAll: 'All statuses',
        customerAll: 'All customers',
        projectAll: 'All projects',
        none: 'None',
        priority: 'Priority',
        priorityAll: 'All priorities',
        type: 'Type',
        typeAll: 'All types',
        reset: 'Reset filters',
      },
      status: {
        NEW: 'New',
        OPEN: 'Open',
        BACKLOG: 'Backlog',
        IN_PROGRESS: 'In progress',
        DONE: 'Done',
        ARCHIVED: 'Archived',
      },
      priorities: {
        must: 'Must',
        should: 'Should',
        could: 'Could',
        wont: "Won't",
      },
      types: {
        aufgabe: 'Task',
        termin: 'Schedule',
        entscheidung: 'Decision',
        anforderung: 'Requirement',
        frage: 'Question',
        risiko: 'Risk',
        annahme: 'Assumption',
        deferred: 'Deferral',
        sonstiges: 'Other',
        blocker: 'Blocker',
        dependency: 'Dependency',
        functional: 'Functional',
        nonfunctional: 'Non-functional',
        constraint: 'Constraint',
        risk: 'Risk',
        assumption: 'Assumption',
        open_question: 'Open question',
      },
      table: {
        text: 'Event',
        customer: 'Customer/Project',
        date: 'Date',
        type: 'Type',
        priority: 'Priority',
        status: 'Status',
        actions: 'Action',
        open: 'Open',
        copyLabel: 'Copy',
        copySuccess: 'Copied.',
        copyError: 'Copy failed.',
      },
      empty: 'No events found.',
      errors: {
        load: 'Could not load events.',
        save: 'Could not save status.',
      },
      toast: {
        statusSaved: 'Status updated.',
      },
    },
    customersProjects: {
      title: 'Customers / Projects',
      description: 'Select a customer and project to view an automatic summary and contextual Q&A.',
      panes: {
        customers: 'Customers',
        projects: 'Projects',
      },
      labels: {
        projects: 'Projects',
        conversations: 'Conversations',
        lastUpdate: 'Updated',
        whatItDoes: 'What does this project do?',
        whatItIsAbout: 'What is it about?',
        nextSteps: 'What comes next?',
        highlights: 'Highlights',
        fallback: 'Fallback',
        sourceStats: 'Sources: analyzed {{used}} / {{total}} conversations',
      },
      states: {
        indexLoading: 'Loading customers and projects…',
        emptyTitle: 'No data yet',
        emptyDescription: 'Please start an upload or a live session.',
        noProjects: 'No projects found for this customer yet.',
        selectProject: 'Please select a project.',
        selectProjectForQa: 'Q&A becomes active once a project is selected.',
        summaryLoading: 'Building summary…',
        summaryChecking: 'Checking updates…',
        noSummary: 'No summary available.',
      },
      errors: {
        indexTitle: 'Could not load customers/projects.',
        indexDescription: 'Please try again.',
        summaryTitle: 'Could not load summary.',
        summaryDescription: 'Please try again.',
      },
      actions: {
        refresh: 'Refresh',
        retry: 'Retry',
        upload: 'Start upload',
      },
      qaTitle: 'Project Q&A',
      qaDescription: 'Ask focused questions for this customer/project. Answers include citations.',
    },
    jobs: {
      title: 'Jobs',
      description: 'The list refreshes automatically every few seconds.',
      refresh: 'Refresh',
      searchLabel: 'Search jobs',
      searchPlaceholder: 'Search by customer, project, status, or content…',
      searchLoading: 'Search is loading…',
      columns: {
        customer: 'Customer',
        project: 'Project',
        meeting: 'Meeting',
        status: 'Status',
        source: 'Source',
        engine: 'Engine',
        updated: 'Created',
        actions: '',
        delete: 'Delete',
      },
      sourceLabel: 'Upload · Mistral',
      openButton: 'Open',
      failedLabel: 'Failed',
      statusUnknown: 'Unknown',
      deleteLabel: 'Delete',
      deleteConfirm: 'Delete this job?',
      deleteError: 'Job could not be deleted.',
      statusLabels: {
        APP_FACTORY_ACTIVE: 'App Factory active',
        LIVE_ACTIVE: 'Live active',
        LIVE: 'Live active',
      },
      noMatches: 'No jobs match your search.',
      empty: 'No uploads yet.',
      loadError: 'Jobs could not be loaded.',
      steps: {
        UPLOADED: 'Upload',
        TRANSCRIBING: 'Transcribing',
        EXTRACTING: 'Extracting',
        READY: 'Ready',
      },
      engineLabels: {
        none: '—',
      },
      sourceLabels: {
        live: 'Live',
        upload: 'Upload',
        transcript: 'Transcript',
        appFactory: 'App Factory',
        unknown: 'Unknown',
      },
    },
    dashboard: {
      welcome: {
        heading: 'Hello {{name}}',
        defaultName: 'you',
        planLabel: 'Your plan:',
        planFallback: 'Unknown',
        priceFallback: '—',
        subtitle: 'Your overview of customers, projects, and jobs.',
        onboardingNote: 'Tip: Open a job to filter topics and edit requirements with a change log.',
        actions: {
          live: 'Start live session',
          upload: 'Upload from file',
          jobs: 'Jobs list',
          profile: 'Profile & settings',
        },
      },
      panels: {
        title: 'Your essential panels',
        subtitle: 'Arrange the workspace the way you need it.',
        adminOnly: 'ADMIN ONLY',
        manageOpen: 'Manage panels',
        manageClose: 'Close panel picker',
        pinnedEmpty: 'Pin panels to display them here.',
        libraryEmpty: 'You already pinned every panel.',
        pin: 'Pin',
        unpin: 'Unpin',
        entries: {
          liveControl: {
            badge: 'Realtime',
            title: 'Live session',
            description: 'Start requirements straight from the conversation and share the room live.',
            activeLabel: 'Active sessions',
            startButton: 'Start session',
            shareButton: 'Share live',
            note: 'Everyone sees the same single source of truth.',
          },
          uploadFlow: {
            badge: 'Audio',
            title: 'Upload pipeline',
            description: 'Visual steps for your audio uploads.',
            steps: ['Upload & validation', 'Transcription', 'Requirement extraction', 'Export in desired format'],
            note: 'If a stage gets stuck you can restart it selectively.',
          },
          exportHandoff: {
            badge: 'Formats',
            title: 'Export & handoff',
            description: 'Deliver results in exactly the artefact your customer expects.',
            formats: ['User stories & Jira import', 'Requirements specification', 'Mechanical/DIN-ready templates'],
            primaryButton: 'Choose format',
            secondaryButton: 'Download',
          },
          pilotProgram: {
            badge: 'Pilot',
            title: 'Pilot & campaigns',
            description: 'Keep pilot customers and social leads in sync.',
            activeLabel: 'Active pilots',
            note: 'Feedback flows straight into AnforderungsAI for fast iterations.',
          },
          deviceReadiness: {
            badge: 'Devices',
            title: 'Device readiness',
            description: 'V1: desktop, tablets, phones. V2: Apple Watch, more devices & Teams plug-in.',
            devices: ['Desktop', 'Tablet', 'Phone'],
            note: 'Plan watch + integrations early to preserve live visibility.',
          },
        },
      },
      insights: {
        title: 'Live insights',
        subtitle: 'Status across customers, projects and live sessions.',
        planFallback: 'Plan',
        cards: {
          total: { label: 'Jobs total', subline: 'incl. live & uploads' },
          running: { label: 'Running', subline: 'with active transcription' },
          completed: { label: 'Ready', subline: 'prepared for export' },
          customers: { label: 'Customers & projects', subline: 'unique customers / projects' },
        },
      },
      portfolio: {
        title: 'Customers & projects',
        subtitle: 'Quick portfolio view based on your latest jobs.',
        customersTitle: 'Customers',
        projectsTitle: 'Projects',
        customerLine: '{{projects}} projects · {{jobs}} jobs',
        projectNamesLabel: 'Projects: {{names}}',
        requirementsTotalLabel: 'Total requirements: {{count}}',
        peopleLabel: 'People: {{names}}',
        projectStatusLabel: 'Status',
        projectStatusActive: 'active',
        projectStatusInactive: 'inactive',
        jobLabel: 'Meeting {{date}}',
        requirementsCountLabel: '{{count}} requirements',
        loadingLabel: '…',
        updatedLabel: 'Updated {{date}}',
        unknownCustomer: 'No customer assigned',
        empty: 'No customer or project data yet.',
      },
      jobs: {
        title: 'Latest jobs',
        countLoading: 'Loading…',
        countEmpty: 'No jobs yet',
        countLabel: 'Showing {{visible}} of {{total}} jobs',
        searchLabel: 'Search',
        searchPlaceholder: 'Customer, project or status',
        filters: {
          all: 'All',
          running: 'Running',
          completed: 'Ready',
          error: 'Error',
        },
        table: {
          customer: 'Customer',
          project: 'Project',
          meeting: 'Meeting',
          status: 'Status',
          updated: 'Created',
          actions: '',
          open: 'Open',
          empty: 'No jobs yet. Start a live session or upload audio.',
          noMatches: 'No job matches your filter.',
        },
        loading: 'Loading…',
        loadError: 'Jobs could not be loaded.',
        loadFailedLabel: 'Load failed',
      },
      statusLabels: {
        RUNNING: 'Running',
        PROCESSING: 'Processing',
        TRANSCRIBING: 'Transcribing',
        EXTRACTING: 'Analyzing',
        COMPLETED: 'Completed',
        READY: 'Ready',
        SUCCESS: 'Completed',
        DONE: 'Completed',
        FAILED: 'Failed',
        ERROR: 'Error',
        CANCELED: 'Canceled',
        PENDING: 'Pending',
      },
    },
    plans: {
      title: 'Plans & billing',
      pillFallback: 'Unknown plan',
      billing: {
        noInfo: 'No billing information available.',
        missing: 'No payment method on file yet.',
        active: 'Billing is active.',
        statusLabel: 'Status',
        subscriptionLabel: 'Subscription',
      },
      card: {
        current: 'Current plan',
        checkout: 'Subscribe',
        activate: 'Activate plan',
      },
      messages: {
        checkoutSuccess: 'Stripe Checkout completed. Your plan will update shortly.',
        checkoutCancel: 'Checkout canceled.',
        updateSuccess: 'Plan updated.',
        updateError: 'Update failed.',
        checkoutCreateError: 'Stripe Checkout could not be created.',
        checkoutFailed: 'Stripe Checkout failed.',
      },
    },
    profile: {
      title: 'Account',
      tabs: {
        account: 'Account info',
        personal: 'Personal details',
        plan: 'Plan',
        export: 'Export templates',
        billing: 'Billing',
        releaseNotes: 'Release notes',
      },
      sectionTitles: {
        account: 'Account information',
        plan: 'Current plan',
        profile: 'Personal details',
        password: 'Change password',
      },
      labels: {
        email: 'Email address',
        billing: 'Current billing:',
        requiredNote: '* Required fields',
        firstName: 'First name *',
        lastName: 'Last name *',
        company: 'Company',
        phone: 'Phone *',
        vatNumber: 'VAT ID (optional)',
        street: 'Street & no. *',
        postalCode: 'Postal code *',
        city: 'City *',
        country: 'Country *',
        profileButton: 'Save profile',
        currentPassword: 'Current password',
        newPassword: 'New password',
        confirmPassword: 'Confirm new password',
        passwordButton: 'Update password',
        deleteButton: 'Delete account',
      },
      releaseNotes: {
        title: 'Release notes',
        subtitle: 'All updates at a glance.',
        empty: 'No release notes yet.',
        loading: 'Loading release notes…',
        loadError: 'Release notes could not be loaded.',
        fallbackTitle: 'Release',
      },
      plan: {
        unknown: 'Unknown plan.',
        togglePicker: 'Change plan',
        noAlternatives: 'No other plans available.',
        alternativesTitle: 'Available plans',
        switchButton: 'Switch',
        currentBadge: 'Current plan',
        pendingLabel: 'Plan change to "{{plan}}" pending.',
        notices: {
          pending: {
            title: 'Plan change pending',
            description: 'Switch to "{{plan}}" is not finished yet. Complete the payment flow to unlock the plan.',
            action: 'Resume checkout',
          },
          payment: {
            title: 'Payment method required',
            description: 'Plan "{{plan}}" needs an active payment method. Please add billing details to unlock uploads.',
            action: 'Add payment method',
          },
        },
        billingFallback: 'Card',
        billingMissing: 'No payment method stored.',
      },
      statuses: {
        planAlreadyActive: 'This plan is already active.',
        unknownPlan: 'Unknown plan.',
        openingCheckout: 'Opening Stripe Checkout…',
        updatingPlan: 'Updating plan…',
        redirectingStripe: 'Redirecting to Stripe…',
        planUpdated: 'Plan updated.',
        planFailed: 'Plan switch failed.',
        profileSaving: 'Saving changes…',
        profileSaved: 'Profile saved.',
        profileError: 'Profile could not be saved.',
        passwordMissingCurrent: 'Current password is required.',
        passwordTooShort: 'New password must be at least 6 characters.',
        passwordMismatch: 'New password and confirmation do not match.',
        passwordUpdating: 'Updating password…',
        passwordUpdated: 'Password updated.',
        passwordError: 'Password could not be updated.',
        deleteConfirm: 'Do you really want to delete your account? This cannot be undone.',
        deleteInProgress: 'Deleting account…',
        deleteSuccess: 'Account deleted. You will be signed out.',
        deleteFailed: 'Account could not be deleted.',
      },
    },
    auth: {
      titles: {
        login: 'Sign in',
        register: 'Create account',
      },
      sections: {
        profile: 'Personal details',
      },
      descriptions: {
        login: 'Sign in with your email to access your uploads.',
        register:
          'Create a new account. Passwords are stored encrypted. You can switch to a paid plan at any time.',
      },
      requiredNote: '* Required fields',
      labels: {
        email: 'Email *',
        password: 'Password *',
        firstName: 'First name *',
        lastName: 'Last name *',
        company: 'Company (optional)',
        phone: 'Phone *',
        vatNumber: 'VAT ID (optional)',
        street: 'Street & no. *',
        postalCode: 'Postal code *',
        city: 'City *',
        country: 'Country *',
      },
      placeholders: {
        company: 'Organization',
        phone: '+1 555 0100',
        vatNumber: 'VAT123456',
        street: '123 Main St',
        postalCode: '10001',
        city: 'City',
        country: 'Country',
      },
      planSelector: {
        title: 'Choose a plan',
        subtitle: 'Pick the plan that fits best. You can change it later.',
        selectButton: 'Select plan',
        selectedButton: 'Selected',
        currentLabel: 'Current plan: {{plan}}',
        unknownPlan: 'Unknown',
      },
      buttons: {
        submit: {
          login: 'Login',
          register: 'Register',
        },
      },
    toggle: {
      login: {
        question: 'No account yet?',
        action: 'Create account',
      },
      register: {
        question: 'Already registered?',
        action: 'Go to login',
      },
    },
    forgotLink: 'Forgot password?',
    forgot: {
      title: 'Forgot password',
      subtitle: 'Enter your email address and we will send you a reset link.',
      emailLabel: 'Email address',
      submit: 'Send reset link',
      sending: 'Sending reset link…',
      sent: 'If an account exists, a reset email has been sent.',
      error: 'Request failed.',
      backToLogin: 'Back to login',
    },
    reset: {
      title: 'Reset password',
      subtitle: 'Set a new password for your account.',
      emailLabel: 'Email address',
      passwordLabel: 'New password',
      confirmLabel: 'Confirm new password',
      submit: 'Save password',
      saving: 'Saving password…',
      success: 'Password updated. You can now sign in.',
      error: 'Password could not be updated.',
      missingToken: 'Reset link is invalid or missing.',
      backToRequest: 'Request a new link',
      backToLogin: 'Back to login',
      passwordTooShort: 'New password must be at least 6 characters.',
      passwordMismatch: 'Passwords do not match.',
    },
    statuses: {
      checkingCredentials: 'Checking credentials…',
      creatingAccount: 'Creating account…',
        successRedirect: 'Success. Redirecting…',
        genericError: 'Action failed.',
        redirectingCheckout: 'Redirecting to payment…',
        checkoutMissingUrl: 'Checkout URL missing in response.',
        checkoutErrorPrefix: 'Checkout error:',
      },
    },
    admin: {
      title: 'Admin console',
      description: 'Overview of every registered account. Changes apply immediately (local demo store).',
      toggle: { open: 'New user', close: 'Cancel' },
      loading: 'Loading users…',
      empty: 'No users yet.',
      addUser: { title: 'Create user', submit: 'Create user' },
      form: {
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email',
        password: 'Password',
        company: 'Company',
        phone: 'Phone',
        vat: 'VAT ID (optional)',
        street: 'Street & no.',
        postalCode: 'Postal code',
        city: 'City',
        country: 'Country',
        plan: 'Plan',
        role: 'Role',
        uiVersion: 'UI version',
        newPassword: 'New password',
        confirmPassword: 'Confirm password',
      },
      placeholders: {
        company: 'Organization',
        phone: '+1 555 0100',
        vat: 'VAT123456',
        street: '123 Main St',
        postalCode: '10001',
        city: 'City',
        country: 'Country',
      },
      table: {
        headers: {
          profile: 'Profile',
          registered: 'Registered',
          plan: 'Plan',
          role: 'Role',
          uiVersion: 'UI version',
          stripe: 'Stripe',
          jobsCurrentMonth: 'Jobs (month)',
          jobsTotal: 'Jobs (total)',
          jobs: 'Jobs',
          jobsCurrentMonthSub: 'per month',
          jobsTotalSub: 'total',
          actions: 'Actions',
        },
        profileFallback: '—',
        editButton: 'Edit',
      },
      stripe: { none: '—', open: 'open' },
      jobStats: {
        title: 'Jobs per month',
        columns: { month: 'Month', count: 'Jobs' },
        empty: 'No jobs recorded.',
      },
      plan: { unknown: 'Unknown plan' },
      roles: { admin: 'Admin', user: 'User' },
      uiVersion: {
        optionLabel: 'Version {{version}}',
        badgeLabel: 'V{{version}}',
        invalid: 'Invalid UI version.',
      },
      actions: { close: 'Close', save: 'Save', cancel: 'Cancel', delete: 'Delete account' },
      modal: { title: 'Edit user' },
      statuses: {
        createRequired: 'Email and password are required.',
        createSuccess: 'User {{email}} created.',
        createFailed: 'Creation failed.',
        passwordTooShort: 'New password must be at least 6 characters.',
        passwordMismatch: 'New password and confirmation do not match.',
        updateSaving: 'Saving changes…',
        updateSuccess: 'Changes saved for {{email}}.',
        updateFailed: 'Update failed.',
        deleteConfirm: 'Do you really want to delete this account?',
        deleteInProgress: 'Archiving account…',
        deleteSuccess: 'Account {{email}} archived.',
        deleteFailed: 'Archiving failed.',
        loadFailed: 'Users could not be loaded.',
      },
    },
    live: {
      title: 'Live transcription',
      buttons: {
        start: 'Start session',
        stop: 'End session',
        settingsOpen: 'Settings',
        settingsClose: 'Hide settings',
        transcriptShow: 'Show live transcript',
        transcriptHide: 'Hide live transcript',
        share: 'Share link',
      },
      fields: {
        customerLabel: 'Customer (optional)',
        customerPlaceholder: 'Customer',
        projectLabel: 'Project (optional)',
        projectPlaceholder: 'Project',
        meetingLabel: 'Meeting date (optional)',
      },
      domainLabel: 'Domain',
      domainOptions: [
        { value: 'enterprise-software', label: 'Enterprise software' },
        { value: 'bau', label: 'Construction' },
        { value: 'maschinenbau', label: 'Mechanical engineering' },
        { value: 'produktentwicklung', label: 'Product development' },
        { value: 'sonstiges', label: 'Other' },
      ],
      domainDefault: 'enterprise-software',
      info: {
        engineLabel: 'Engine',
        languageLabel: 'Language',
        languageUnknown: 'Not detected yet.',
        languageManualSuffix: ' (manual)',
        statusReady: 'Ready. Waiting for start.',
        extractionWarning:
          'Requirements could not be fully validated. Showing the last confirmed results.',
        footer:
          'Start a live session: microphone and system audio stream in parallel and are transcribed in real time. Allow browser access to both audio sources.',
      },
      audio: {
        systemLabel: 'System',
        micLabel: 'Microphone',
        sourceSystem: 'System audio active',
        sourceMic: 'Microphone active',
      },
      audioModes: [
        { code: 'mix', label: 'System + Mic', description: 'Capture system audio and microphone together' },
      ],
      requirements: {
        title: 'Detected requirements',
        empty: 'No requirements extracted yet.',
        fallbackType: 'Other',
        shareMissing: 'No requirements available yet.',
        shareCopied: 'Link copied!',
        shareCreateError: 'Could not create link.',
        shareClipboardError: 'Copy failed. <a href="{{url}}" target="_blank">Open link</a>',
      },
      transcript: {
        empty: 'No audio received yet.',
      },
      statuses: {
        settingsLockedLanguage: 'Session running – language selection locked.',
        settingsLockedEngine: 'Session running – engine cannot be changed.',
        settingsLockedAudio: 'Session running – audio source locked.',
        settingsLockedDomain: 'Session running – domain locked.',
        connecting: 'Connecting…',
        systemAudioUnsupported: 'System audio unavailable – recording microphone only.',
        chunkBuffered: 'Audio fragment incomplete – waiting for more data…',
        chunkSkipped: 'Audio chunk skipped – incomplete.',
        sessionStreaming: 'Session live – streaming.',
        chunkSendFailed: 'Upload failed. Trying again…',
        waitingAudio: 'Session live – waiting for first audio data…',
        waitingAudioMic: 'Session live – waiting for first audio data (mic only).',
        stopError: 'Could not stop the session.',
        startError: 'Session could not be started.',
      },
      appFactory: {
        title: 'App Factory guide',
        description: 'Run structured discovery calls directly from the live console.',
        sessionSelectLabel: 'Select session',
        sessionSelectPlaceholder: 'Choose a session…',
        sessionEmpty: 'No App Factory sessions yet.',
      buttons: {
        refresh: 'Refresh',
        newSession: 'New session',
        analyze: 'Analyze progress',
        sync: 'Sync requirements',
        open: 'Open session',
      },
      sessionLabel: 'Session',
      customerLabelShort: 'Customer',
      projectLabelShort: 'Project',
      autoSyncLabel: 'Automatically sync new requirements',
        requirementsTitle: 'Live requirements',
        requirementsHint: 'Pick a session to sync requirements.',
        requirementsSummary: '{{count}} requirements ready.',
        guidelineEmpty: 'No guideline loaded.',
        status: {
          selectSession: 'Please select a session.',
          loadingSessions: 'Loading sessions…',
          loadingSession: 'Loading guideline…',
          syncEmpty: 'No requirements yet.',
          syncing: 'Syncing requirements…',
          syncSuccess: 'Requirements synced.',
          analyzing: 'Analyzing…',
          updatedAt: 'Updated at {{time}}',
        },
        errors: {
          load: 'Failed to load sessions.',
          create: 'Could not create session.',
          analyze: 'Progress analysis failed.',
          sync: 'Failed to sync requirements.',
          session: 'Could not load session.',
          init: 'App Factory failed to initialize.',
        },
        prompts: {
          customer: 'Optional: enter customer ID',
          project: 'Optional: enter project ID',
        },
        creating: 'Creating session…',
      },
    },
    liveShare: {
      title: 'Shared requirements',
      loadError: 'The link could not be read. Please request it again.',
      sessionLabel: 'Session on {{date}}',
      sharedBy: {
        prefix: 'Shared by',
        unknown: 'unknown',
        companySeparator: ' – ',
      },
      buttons: {
        csv: 'Download CSV',
        md: 'Download Markdown',
      },
      empty: 'No requirements were shared.',
      note: 'Note: this view shows a snapshot of the live session. Realtime updates require the upcoming backend.',
      card: {
        fallbackType: 'Other',
      },
      download: {
        csvHeaders: {
          type: 'Type',
          text: 'Text',
        },
        markdown: {
          title: 'Detected requirements',
          sessionLabel: 'Session',
        },
      },
    },
    conversation: {
      title: 'Conversation',
      loading: 'Loading results…',
      backToJobs: 'Back to jobs',
      backLabel: 'Back',
      header: {
        status: 'Status',
        conversationId: 'Conversation ID',
        customer: 'Customer',
        project: 'Project',
        meeting: 'Meeting',
        created: 'Created',
        updated: 'Updated',
      },
      progress: {
        label: 'Status',
        phases: {
          uploaded: 'Uploaded',
          processing: 'Processing',
          transcribing: 'Transcribing',
          extracting: 'Extracting events',
          live: 'Live session active',
          finalizing: 'Finalizing',
          finalized: 'Completed',
          failed: 'Failed',
          unknown: 'Unknown',
        },
      },
      delete: {
        button: 'Delete job',
        confirm: 'Delete this job? All requirements, transcripts and files will be removed.',
        inProgress: 'Deleting…',
        success: 'Job deleted.',
        error: 'Job could not be deleted.',
      },
      metaEdit: {
        button: 'Edit',
        save: 'Save',
        cancel: 'Cancel',
        inProgress: 'Saving…',
        error: 'Update failed.',
      },
      filters: {
        topics: 'Topics',
        topicsAll: 'All topics',
        noTopics: 'No topics available.',
        contentLabel: 'Show',
        contentAll: 'All',
        priorityLabel: 'Priority',
        priorityAll: 'All priorities',
        categoryLabel: 'Category',
        categoryAll: 'All categories',
        searchLabel: 'Search',
        searchPlaceholder: 'Text, ID, or keyword',
        clear: 'Reset filters',
        countLabel: '{{count}} results',
        noResults: 'No item matches your filters.',
        emptyAll: 'No requirements or user stories yet.',
        priorityOptions: {
          high: 'High',
          medium: 'Medium',
          low: 'Low',
          must: 'Must',
          should: 'Should',
          could: 'Could',
          wont: "Won't",
        },
        topicLabels: {
          functional: 'Functional',
          non_functional: 'Non-functional',
          business: 'Business',
          knowledge_management: 'Knowledge Management',
          system_integration: 'System Integration',
          lifecycle_tracking: 'Lifecycle Tracking',
          onboarding_enablement: 'Onboarding Enablement',
          global_enablement: 'Global Enablement',
          data_governance: 'Data Governance',
          analytics_automation: 'Analytics Automation',
          service_quality: 'Service Quality',
          other: 'Other',
        },
      },
      sections: {
        requirements: 'Requirements',
        userStories: 'User stories',
        deferredDecisions: 'Deferred decisions',
      },
      summary: {
        title: 'Executive Summary',
        download: 'Download summary',
        downloadDescription: 'Executive Summary (Markdown)',
        collapse: 'Collapse',
        expand: 'Expand',
        overview: 'Overview',
        keyRequirements: 'Key requirements',
        openDecisions: 'Open decisions',
        risks: 'Risks',
        nextSteps: 'Next steps',
        downloadSuccess: 'Executive Summary download started.',
        downloadError: 'Executive Summary could not be downloaded.',
      },
      exports: {
        csv: 'CSV export',
        md: 'Markdown export',
      },
      requirements: {
        empty: 'No requirements yet.',
        rationale: 'Rationale',
        source: 'Source',
        speaker: 'Speaker',
        tags: 'Tags',
      },
      edit: {
        editButton: 'Edit',
        copyButton: 'Copy',
        copySuccess: 'Copied.',
        copyError: 'Copy failed.',
        saveButton: 'Save',
        cancelButton: 'Cancel',
        priorityUnset: 'No priority',
        fields: {
          text: 'Text',
          story: 'User story',
          role: 'Role',
          goal: 'Goal',
          benefit: 'Benefit',
          acceptance: 'Acceptance criteria',
          category: 'Category',
          priority: 'Priority',
          topics: 'Topics',
        },
        topicsPlaceholder: 'e.g. Security, Reporting',
      },
      userStories: {
        empty: 'No user stories yet.',
        role: 'Role',
        goal: 'Goal',
        benefit: 'Benefit',
        acceptance: 'Acceptance criteria',
        priorityFallback: 'Medium',
      },
      log: {
        title: 'Change log',
        empty: 'No changes yet.',
        note: 'Changes are stored in the database.',
        emptyValue: '—',
      },
    },
    qaChat: {
      title: 'Q&A chat with citations',
      description: 'Ask questions about requirements and transcripts. Answers include traceable evidence.',
      requirementsTitle: 'Q&A across all requirements',
      requirementsDescription: 'Current filters are used as context (customer, project, period).',
      conversationTitle: 'Q&A for this conversation',
      conversationDescription: 'Ask specifically about this conversation with sources including speaker and time.',
      roles: {
        user: 'You',
        assistant: 'Assistant',
      },
      state: {
        loading: 'Loading chat…',
        empty: 'No messages yet. Ask your first question.',
      },
      input: {
        placeholder: 'Example: What color should the house be?',
      },
      actions: {
        send: 'Send',
        newThread: 'New thread',
        deleteThread: 'Delete',
        includeGeneralAnswer: '+ General knowledge (Test)',
        searchGlobal: 'Search globally',
      },
      labels: {
        generalAnswer: 'General knowledge (Test)',
      },
      feedback: {
        threadDeleted: 'Thread deleted.',
      },
      citations: {
        title: 'Sources',
        unknownSpeaker: 'Unknown speaker',
        unknownCustomer: 'Unknown customer',
        unknownProject: 'Unknown project',
      },
      errors: {
        default: 'Request failed.',
        send: 'Message could not be sent.',
        loadThread: 'Thread could not be loaded.',
        createThread: 'Thread could not be created.',
        deleteThread: 'Thread could not be deleted.',
        loadThreads: 'Threads could not be loaded.',
      },
    },
    imprint: {
      title: 'Imprint',
      intro: 'This legal notice applies to the AnforderungsAI service available at https://anforderungsai.de.',
      tabs: {
        imprint: 'Imprint',
        agb: 'Terms (AGB)',
        privacy: 'Privacy',
      },
      provider: {
        heading: 'Provider',
        nameLabel: 'Name',
        name: IMPRINT_DETAILS.name,
        addressLabel: 'Address',
        addressLines: [IMPRINT_DETAILS.street, IMPRINT_DETAILS.postalCodeCity, IMPRINT_DETAILS.country],
        registerLabel: 'Commercial register',
        registerNote: 'No commercial register entry exists.',
      },
      contact: {
        heading: 'Contact',
        emailLabel: 'Email',
        email: IMPRINT_DETAILS.email,
        phoneLabel: 'Phone',
        phone: 'Not published – please reach out via email.',
      },
      vat: {
        heading: 'VAT ID',
        text: `VAT ID (Section 27a German VAT Act): ${IMPRINT_DETAILS.taxId}`,
      },
      supervision: {
        heading: 'Supervisory authority',
        text: 'No specific supervisory authority applies.',
      },
      profession: {
        heading: 'Professional regulations',
        text: 'No regulated professions or special professional rules apply.',
      },
      dispute: {
        heading: 'Dispute resolution / EU ODR',
        text: 'The European Commission provides an Online Dispute Resolution (ODR) platform:',
        platformLabel: 'https://ec.europa.eu/consumers/odr',
        participation: 'We are neither obliged nor willing to participate in consumer dispute resolution before a consumer arbitration board.',
      },
      editorial: {
        heading: 'Responsible under Sec. 18 (2) MStV',
        text: `${IMPRINT_DETAILS.name}, same address.`,
      },
      notes: {
        lastUpdated: 'Last updated: January 2025',
      },
    },
  },
  fr: {
    common: {
      loading: 'Chargement…',
      noData: 'Pas encore de données.',
      error: 'Impossible de charger les données.',
      authRequired: 'Connecte-toi pour voir ces données.',
      download: {
        button: 'Télécharger',
        title: 'Télécharger',
        subtitle: 'Choisis un format et lance l’export.',
        formatLabel: 'Format',
        templateLabel: 'Modèle',
        standardOption: 'Export standard',
        manageTemplates: 'Gérer les modèles',
        noTemplates: 'Aucun modèle disponible.',
        cancel: 'Annuler',
        confirm: 'Démarrer le téléchargement',
        loading: 'Téléchargement…',
      },
    },
    nav: {
      home: 'Accueil',
      dashboard: 'Tableau de bord',
      requirements: 'Exigences',
      customersProjects: 'Clients / Projets',
      upload: 'Import',
      newRequirements: 'Nouvel événement',
      jobs: 'Jobs',
      live: 'Live',
      appFactory: 'App Factory',
      profile: 'Compte',
      admin: 'Admin',
      imprint: 'Mentions légales',
      login: 'Connexion',
      register: 'Inscription',
    },
    sidebar: {
      tagline: 'Cockpit de spécifications nouvelle génération',
      navigationLabel: 'Navigation',
      accessLabel: 'Accès',
      themeLabel: 'Thème',
      languageLabel: 'Langue',
      loggedInLabel: 'Connecté en tant que',
      logout: 'Se déconnecter',
      logoutDemo: 'Quitter la démo',
      menuOpen: 'Ouvrir le menu',
      menuClose: 'Fermer le menu',
      languages: createLanguageLabels(),
    },
    releaseNotes: {
      title: 'Nouveautés',
      subtitle: 'Changements depuis ta dernière connexion.',
      confirmButton: "J'ai compris",
      ackInProgress: 'Confirmation en cours…',
      ackError: 'Confirmation échouée.',
      fallbackTitle: 'Release',
    },
    home: {
      heroEyebrow: 'Gestion des exigences pour équipes produit et projet',
      heroTitle: 'De la conversation à des exigences claires en quelques minutes.',
      heroDescription: `AnforderungsAI extrait les exigences à partir d’un large éventail de sources — par exemple des entretiens en direct, des ateliers et des visioconférences, ainsi que des documents, des transcriptions, des fichiers audio et vidéo.
Les contenus sont automatiquement structurés, contextualisés et priorisés, puis disponibles dans plusieurs formats pour la suite du traitement et l’export.`,
      heroMore:
        'La pipeline transcrit audio et vidéo, identifie les affirmations pertinentes et les relie aux métadonnées. Le résultat : un catalogue d’exigences traçable qui aide à la revue, à la priorisation et à la passation.',
      heroToggleMore: 'Détails du produit',
      heroToggleLess: 'Masquer les détails',
      ctas: {
        primary: 'Demander une démo',
        secondary: 'Voir la démo',
        pricing: 'Voir les tarifs',
        register: 'Demander une démo',
      },
      trust: [
        'Conforme RGPD',
        'Hébergement UE',
        'CSV, Markdown & JSON',
        'Live ou upload',
      ],
      featuresTitle: 'Ce qu’AnforderungsAI délivre',
      featuresSubtitle: 'Pour les équipes qui veulent transformer des conversations en exigences exploitables.',
      features: [
        {
          title: 'Workflows live et fichiers',
          description: 'Lance une session live ou téléverse audio, vidéo ou transcript.',
        },
        {
          title: 'Extraction avec contexte',
          description: 'Chaque exigence reste traçable avec source, justification et tags.',
        },
        {
          title: 'Structure & priorisation',
          description: 'Types, statuts et priorités sont propres et filtrables.',
        },
        {
          title: 'Résultats prêts à exporter',
          description: 'CSV, Markdown ou JSON via modèles — prêts pour doc et backlog.',
        },
        {
          title: 'Partage en équipe',
          description: 'Les résultats se partagent par lien et se valident ensemble.',
        },
        {
          title: 'Protection des données en UE',
          description: 'Traitement et stockage adaptés aux exigences européennes.',
        },
      ],
      stepsTitle: 'Comment ça marche',
      stepsSubtitle: 'En trois étapes claires de la source au résultat exploitable.',
      steps: [
        {
          title: 'Choisir une source',
          description: 'Sélectionne interview, atelier ou document existant.',
        },
        {
          title: 'Analyse en cours',
          description: 'Transcription et extraction génèrent des exigences contextualisées.',
        },
        {
          title: 'Transmettre les résultats',
          description: 'Vérifier, prioriser et exporter pour la passation.',
        },
      ],
      demo: {
        title: 'Tester tout de suite',
        description:
          'Téléverse un fichier texte, CSV ou JSON. Nous affichons une exigence détectée en aperçu. Pour l’analyse complète, un login est nécessaire.',
        button: 'Vérifier le fichier',
        note: 'La démo s’exécute localement dans ton navigateur. Rien n’est uploadé.',
        error: 'Une erreur est survenue lors de la lecture du fichier.',
        noRequirement: 'Aucune exigence détectée.',
        downloadLabel: 'Télécharger le fichier exemple',
        fileLabel: 'Choisir un fichier',
        formatsHint: 'Formats : TXT, MD, CSV, JSON.',
        customerLabel: 'Client',
        projectLabel: 'Projet',
        snippetLabel: 'Extrait',
        successTitle: 'Résultat d’exemple',
        successNote: 'Pour les résultats complets, connecte-toi.',
        selectFileWarning: 'Merci de choisir un fichier.',
        loading: 'Analyse du fichier…',
      },
      demoFile: {
        customer: 'Nordachse Mobility',
        project: 'Hub de recharge UE',
        snippet: 'Le hub de recharge doit supporter des stations modulaires et réagir aux pics en moins de 2 minutes.',
      },
      pricingTitle: 'Tarifs',
      pricingSubtitle: 'Facturation transparente, ajustable à tout moment. Paiements via Stripe.',
      pricingNote: 'Pour un pilote ou un besoin enterprise, nous proposons des offres sur mesure.',
      pricingDetails: {
        quota: {
          monthly: 'Uploads par mois inclus',
          per_job: 'Facturation flexible par upload traité',
          unlimited: 'Uploads illimités, traitement prioritaire',
          default: 'Utilisation flexible selon les besoins',
        },
        billingRequired: 'Données de paiement requises',
        free: 'Commencer gratuitement',
        actionLabel: 'Choisir le plan',
        unitPriceLabel: '€3 / Nutzung',
      },
      testimonialsTitle: 'Retours de projets',
      testimonialsSubtitle:
        'Des équipes produit, projet et conseil utilisent AnforderungsAI pour capter les exigences de façon cohérente et accélérer la passation.',
      testimonials: [
        {
          quote: 'Nous obtenons des exigences avec source et évitons des retours en revue.',
          author: 'Cheffe produit, B2B SaaS',
        },
        {
          quote: 'Les modèles d’export nous donnent un format propre pour la doc et le backlog.',
          author: 'Scrum master, agence digitale',
        },
        {
          quote: 'Surtout en phase amont, nous structurons rapidement les retours des parties prenantes.',
          author: 'Chef de projet IT, ETI',
        },
      ],
      contactButton: 'Demander une démo',
      demoButton: 'Voir la démo',
      faqTitle: 'Questions fréquentes',
      faqSubtitle: 'Des réponses courtes pour faciliter la décision.',
      faq: [
        {
          question: 'Quels formats sont pris en charge ?',
          answer: 'Audio/vidéo (MP3, MP4, WAV, QTA) et transcripts (TXT, MD, JSON, CSV, VTT, SRT).',
        },
        {
          question: 'Que deviennent mes données ?',
          answer: 'Le fichier de démo est traité localement dans le navigateur. Pour l’analyse complète, le fichier est uploadé et traité dans ton workspace.',
        },
        {
          question: 'À quoi ressemblent les résultats ?',
          answer: 'Exigences structurées avec source, tags et user stories optionnelles. Export CSV, Markdown ou JSON.',
        },
        {
          question: 'En combien de temps ai-je les résultats ?',
          answer: 'Généralement en quelques minutes, selon la longueur et le format de la source.',
        },
      ],
      ctaTitle: 'Apporter de la clarté à vos exigences',
      ctaSubtitle: 'Réserve une démo ou teste l’extraction avec ton propre fichier.',
      ctaPrimary: 'Demander une démo',
      ctaSecondary: 'Voir la démo',
    },
    upload: {
      title: 'Téléverser un fichier',
      formatsHint: 'Formats acceptés : MP3, MP4, WAV, QTA.',
      description:
        'Formats pris en charge : MP3, MP4, WAV, QTA. Après l’upload, Transcribe démarre automatiquement.',
      fileLabel: 'Fichier audio ou vidéo',
      engineLabel: 'Moteur de transcription',
      engineDescription: 'Choisis le moteur de reconnaissance vocale.',
      engines: {
        mistral: 'Mistral (Voxtral)',
        aws: 'AWS Transcribe',
      },
      customerLabel: 'Client (optionnel)',
      projectLabel: 'Projet (optionnel)',
      meetingLabel: 'Date de réunion (optionnel)',
      customerPlaceholder: 'Identifiant client',
      projectPlaceholder: 'Nom du projet',
      domainLabel: 'Domaine',
      domainOptions: [
        { value: 'enterprise-software', label: "Logiciel d'entreprise" },
        { value: 'bau', label: 'Construction' },
        { value: 'maschinenbau', label: 'Génie mécanique' },
        { value: 'produktentwicklung', label: 'Développement produit' },
        { value: 'sonstiges', label: 'Autre' },
      ],
      domainDefault: 'enterprise-software',
      button: "Lancer l'upload",
      selectFileWarning: 'Merci de choisir un fichier.',
      creatingLink: 'Génération du lien…',
      uploading: 'Téléversement…',
      uploadDone: 'Upload terminé.',
      uploadError: 'Échec du téléversement.',
      planNote: 'Forfait actuel :',
      planNoteCta: 'Gérer le plan',
      listTitle: 'Uploads récents',
      progressLabel: 'Progression',
      successTitle: 'Upload réussi',
      conversationLabel: 'ID de conversation',
      viewJobs: 'Voir les jobs',
      copyId: "Copier l'ID",
      copySuccess: 'Copié !',
      copyError: 'Impossible de copier',
      successNote: 'Le traitement continue en arrière-plan. Consulte la vue Jobs.',
      statuses: {
        running: 'En traitement',
        success: 'Prêt à être exporté',
        error: 'Erreur',
      },
      transcript: {
        title: 'Importer un transcript',
        description: 'Tu as déjà un transcript texte ou JSON ? Téléverse-le et passe directement à l’extraction.',
        fileLabel: 'Fichier transcript',
        formatsHint: 'Formats pris en charge : TXT, MD, JSON, CSV, VTT, SRT, PDF, DOCX.',
        button: 'Traiter le transcript',
        successTitle: 'Transcript importé',
        successNote: 'L’extraction démarre immédiatement à partir de ton transcript.',
        status: {
          readingPdf: 'Lecture du PDF…',
          pdfEmpty: 'Le PDF ne contient pas de texte lisible.',
          readingDocx: 'Lecture du document Word…',
          docxEmpty: 'Le document Word ne contient pas de texte lisible.',
          docUnsupported: 'Le format .doc n’est pas pris en charge. Enregistre en .docx.',
          finalizing: 'Lancement de l’extraction…',
          uploadError: 'Échec du téléversement du transcript.',
        },
      },
    },
    newRequirements: {
      title: 'Nouvelles exigences',
      subtitle: 'Lance une session live ou importe des fichiers/transcripts pour créer des exigences.',
      uploadTitle: 'Téléverser audio / vidéo',
      live: {
        badge: 'Live',
        title: 'Transcription en direct',
        description: 'Capture les conversations en temps réel et extrais les exigences immédiatement.',
        note: 'Audio micro ou système transmis en direct.',
      },
    },
    requirementsList: {
      title: 'Événements',
      subtitle: 'Tous les événements par client, projet et période.',
      summary: {
        total: 'Total',
      },
      actions: {
        refresh: 'Actualiser',
        exportCsv: 'Export CSV',
        exportMd: 'Export Markdown',
        exportJson: 'Export JSON',
        exportXlsx: 'Export Excel',
        exportDocx: 'Export Word',
        download: 'Télécharger',
      },
      filters: {
        search: 'Recherche',
        searchPlaceholder: 'Texte, tags, catégories...',
        customer: 'Client',
        customerPlaceholder: 'Identifiant client',
        project: 'Projet',
        projectPlaceholder: 'Nom du projet',
        from: 'Du',
        to: 'Au',
        status: 'Statut',
        statusAll: 'Tous les statuts',
        customerAll: 'Tous les clients',
        projectAll: 'Tous les projets',
        none: 'Aucun',
        priority: 'Priorité',
        priorityAll: 'Toutes les priorités',
        type: 'Type',
        typeAll: 'Tous les types',
        reset: 'Réinitialiser',
      },
      status: {
        NEW: 'Nouveau',
        OPEN: 'Ouvert',
        BACKLOG: 'Backlog',
        IN_PROGRESS: 'En cours',
        DONE: 'Terminé',
        ARCHIVED: 'Archivé',
      },
      priorities: {
        must: 'Doit',
        should: 'Devrait',
        could: 'Pourrait',
        wont: 'Ne fera pas',
      },
      types: {
        aufgabe: 'Tâche',
        termin: 'Échéance',
        entscheidung: 'Décision',
        anforderung: 'Exigence',
        frage: 'Question',
        risiko: 'Risque',
        annahme: 'Hypothèse',
        deferred: 'Report',
        sonstiges: 'Autre',
        blocker: 'Bloqueur',
        dependency: 'Dépendance',
        functional: 'Fonctionnel',
        nonfunctional: 'Non fonctionnel',
        constraint: 'Contrainte',
        risk: 'Risque',
        assumption: 'Hypothèse',
        open_question: 'Question ouverte',
      },
      table: {
        text: 'Événement',
        customer: 'Client/Projet',
        date: 'Date',
        type: 'Type',
        priority: 'Priorité',
        status: 'Statut',
        actions: 'Action',
        open: 'Ouvrir',
        copyLabel: 'Copier',
        copySuccess: 'Copié.',
        copyError: 'Copie échouée.',
      },
      empty: 'Aucun événement trouvé.',
      errors: {
        load: 'Impossible de charger les événements.',
        save: 'Impossible d’enregistrer le statut.',
      },
      toast: {
        statusSaved: 'Statut mis à jour.',
      },
    },
    customersProjects: {
      title: 'Clients / Projets',
      description: 'Choisis un client et un projet pour voir un résumé automatique et un Q&A contextuel.',
      panes: {
        customers: 'Clients',
        projects: 'Projets',
      },
      labels: {
        projects: 'Projets',
        conversations: 'Conversations',
        lastUpdate: 'Mise à jour',
        whatItDoes: 'Que fait ce projet ?',
        whatItIsAbout: 'De quoi s’agit-il ?',
        nextSteps: 'Quelles sont les prochaines étapes ?',
        highlights: 'Points clés',
        fallback: 'Fallback',
        sourceStats: 'Sources : {{used}} / {{total}} conversations analysées',
      },
      states: {
        indexLoading: 'Chargement des clients et projets…',
        emptyTitle: 'Pas encore de données',
        emptyDescription: 'Démarre un upload ou une session live.',
        noProjects: 'Aucun projet trouvé pour ce client.',
        selectProject: 'Sélectionne un projet.',
        selectProjectForQa: 'Le Q&A devient actif dès qu’un projet est sélectionné.',
        summaryLoading: 'Résumé en cours de génération…',
        summaryChecking: 'Vérification des mises à jour…',
        noSummary: 'Aucun résumé disponible.',
      },
      errors: {
        indexTitle: 'Impossible de charger clients/projets.',
        indexDescription: 'Réessaie.',
        summaryTitle: 'Impossible de charger le résumé.',
        summaryDescription: 'Réessaie.',
      },
      actions: {
        refresh: 'Actualiser',
        retry: 'Réessayer',
        upload: 'Démarrer un upload',
      },
      qaTitle: 'Q&A du projet',
      qaDescription: 'Pose des questions ciblées pour ce client/projet. Les réponses incluent des sources.',
    },
    jobs: {
      title: 'Jobs',
      description: 'La liste se rafraîchit automatiquement toutes les quelques secondes.',
      refresh: 'Actualiser',
      searchLabel: 'Rechercher des jobs',
      searchPlaceholder: 'Recherche par client, projet, statut ou contenu…',
      searchLoading: 'Recherche en cours…',
      columns: {
        customer: 'Client',
        project: 'Projet',
        meeting: 'Réunion',
        status: 'Statut',
        source: 'Source',
        engine: 'Moteur',
        updated: 'Créé',
        actions: '',
        delete: 'Supprimer',
      },
      sourceLabel: 'Upload · Mistral',
      openButton: 'Ouvrir',
      failedLabel: 'Échec',
      statusUnknown: 'Inconnu',
      deleteLabel: 'Supprimer',
      deleteConfirm: 'Supprimer ce job ?',
      deleteError: 'Le job n’a pas pu être supprimé.',
      statusLabels: {
        APP_FACTORY_ACTIVE: 'App Factory actif',
        LIVE_ACTIVE: 'Live actif',
        LIVE: 'Live actif',
      },
      noMatches: 'Aucun job ne correspond à ta recherche.',
      empty: 'Aucun upload pour le moment.',
      loadError: 'Impossible de charger les jobs.',
      steps: {
        UPLOADED: 'Upload',
        TRANSCRIBING: 'Transcription',
        EXTRACTING: 'Extraction',
        READY: 'Terminé',
      },
      engineLabels: {
        none: '—',
      },
      sourceLabels: {
        live: 'Live',
        upload: 'Upload',
        transcript: 'Transcript',
        appFactory: 'App Factory',
        unknown: 'Inconnu',
      },
    },
    dashboard: {
      welcome: {
        heading: 'Bonjour {{name}}',
        defaultName: 'toi',
        planLabel: 'Ton forfait :',
        planFallback: 'Inconnu',
        priceFallback: '—',
        subtitle: 'Vue d’ensemble des clients, projets et jobs.',
        onboardingNote: 'Astuce : ouvre un job pour filtrer les thèmes et modifier les exigences avec journal.',
        actions: {
          live: 'Lancer le live',
          upload: 'Upload depuis un fichier',
          jobs: 'Liste des jobs',
          profile: 'Profil & paramètres',
        },
      },
      panels: {
        title: 'Tes panneaux clés',
        subtitle: 'Organise ton espace de travail comme tu le souhaites.',
        adminOnly: 'ADMIN ONLY',
        manageOpen: 'Gérer les panneaux',
        manageClose: 'Fermer le sélecteur',
        pinnedEmpty: 'Épingle des panneaux pour les voir ici.',
        libraryEmpty: 'Tu as déjà tout épinglé.',
        pin: 'Épingler',
        unpin: 'Désépingler',
        entries: {
          liveControl: {
            badge: 'Temps réel',
            title: 'Session live',
            description: 'Lance les exigences directement pendant la conversation et partage la salle.',
            activeLabel: 'Sessions actives',
            startButton: 'Démarrer la session',
            shareButton: 'Partager le live',
            note: 'Tout le monde voit la même source de vérité.',
          },
          uploadFlow: {
            badge: 'Audio',
            title: "Pipeline d'upload",
            description: 'Étapes visuelles pour tes uploads audio.',
            steps: ['Upload & validation', 'Transcription', 'Extraction des exigences', 'Export au format souhaité'],
            note: "Si une étape bloque, relance-la individuellement.",
          },
          exportHandoff: {
            badge: 'Formats',
            title: 'Export & handoff',
            description: 'Livre exactement l’artefact attendu par ton client.',
            formats: ['User stories & import Jira', 'Cahier des charges', 'Modèles DIN / mécanique'],
            primaryButton: 'Choisir un format',
            secondaryButton: 'Télécharger',
          },
          pilotProgram: {
            badge: 'Pilotes',
            title: 'Programme pilote & campagnes',
            description: 'Garde clients pilotes et leads sociaux alignés.',
            activeLabel: 'Pilotes actifs',
            note: 'Le feedback arrive directement dans AnforderungsAI pour itérer vite.',
          },
          deviceReadiness: {
            badge: 'Appareils',
            title: 'Préparation appareils',
            description: 'V1 : desktop, tablettes, mobiles. V2 : Apple Watch, autres devices & plugin Teams.',
            devices: ['Desktop', 'Tablette', 'Mobile'],
            note: 'Planifie tôt les montres & intégrations pour garder la visibilité live.',
          },
        },
      },
      insights: {
        title: 'Aperçus live',
        subtitle: 'Statut des clients, projets et sessions en cours.',
        planFallback: 'Forfait',
        cards: {
          total: { label: 'Jobs au total', subline: 'incl. live & uploads' },
          running: { label: 'En cours', subline: 'transcription active' },
          completed: { label: 'Terminés', subline: 'prêts pour export' },
          customers: { label: 'Clients & projets', subline: 'clients / projets uniques' },
        },
      },
      portfolio: {
        title: 'Clients & projets',
        subtitle: 'Vue rapide basée sur tes derniers jobs.',
        customersTitle: 'Clients',
        projectsTitle: 'Projets',
        customerLine: '{{projects}} projets · {{jobs}} jobs',
        projectNamesLabel: 'Projets : {{names}}',
        requirementsTotalLabel: 'Exigences au total : {{count}}',
        peopleLabel: 'Personnes : {{names}}',
        projectStatusLabel: 'Statut',
        projectStatusActive: 'actif',
        projectStatusInactive: 'inactif',
        jobLabel: 'Réunion {{date}}',
        requirementsCountLabel: '{{count}} exigences',
        loadingLabel: '…',
        updatedLabel: 'Mis à jour le {{date}}',
        unknownCustomer: 'Sans client',
        empty: 'Aucune donnée client ou projet pour le moment.',
      },
      jobs: {
        title: 'Derniers jobs',
        countLoading: 'Chargement…',
        countEmpty: 'Aucun job',
        countLabel: '{{visible}} sur {{total}} jobs affichés',
        searchLabel: 'Recherche',
        searchPlaceholder: 'Client, projet ou statut',
        filters: {
          all: 'Tous',
          running: 'En cours',
          completed: 'Terminés',
          error: 'Erreur',
        },
        table: {
          customer: 'Client',
          project: 'Projet',
          meeting: 'Réunion',
          status: 'Statut',
          updated: 'Créé',
          actions: '',
          open: 'Ouvrir',
          empty: 'Pas encore de jobs. Lance une session live ou importe un audio.',
          noMatches: 'Aucun job ne correspond au filtre.',
        },
        loading: 'Chargement…',
        loadError: 'Impossible de charger les jobs.',
        loadFailedLabel: 'Erreur de chargement',
      },
      statusLabels: {
        RUNNING: 'En cours',
        PROCESSING: 'Traitement',
        TRANSCRIBING: 'Transcription',
        EXTRACTING: 'Analyse',
        COMPLETED: 'Terminé',
        READY: 'Prêt',
        SUCCESS: 'Terminé',
        DONE: 'Terminé',
        FAILED: 'Échec',
        ERROR: 'Erreur',
        CANCELED: 'Annulé',
        PENDING: 'En attente',
      },
    },
    plans: {
      title: 'Plans & facturation',
      pillFallback: 'Plan inconnu',
      billing: {
        noInfo: 'Aucune information disponible.',
        missing: 'Aucun moyen de paiement enregistré.',
        active: 'Facturation active.',
        statusLabel: 'Statut',
        subscriptionLabel: 'Abonnement',
      },
      card: {
        current: 'Plan actuel',
        checkout: "S'abonner",
        activate: 'Activer le plan',
      },
      messages: {
        checkoutSuccess: 'Stripe Checkout terminé. Le plan sera mis à jour sous peu.',
        checkoutCancel: 'Paiement annulé.',
        updateSuccess: 'Plan mis à jour.',
        updateError: 'La mise à jour a échoué.',
        checkoutCreateError: 'Impossible de créer le Stripe Checkout.',
        checkoutFailed: 'Échec du Stripe Checkout.',
      },
    },
    profile: {
      title: 'Compte',
      tabs: {
        account: 'Infos du compte',
        personal: 'Données personnelles',
        plan: 'Forfait',
        export: "Modèles d'export",
        billing: 'Facturation',
        releaseNotes: 'Notes de version',
      },
      sectionTitles: {
        account: 'Informations du compte',
        plan: 'Plan actuel',
        profile: 'Données personnelles',
        password: 'Changer le mot de passe',
      },
      labels: {
        email: 'Adresse e-mail',
        billing: 'Facturation en cours :',
        requiredNote: '* Champs requis',
        firstName: 'Prénom *',
        lastName: 'Nom *',
        company: 'Entreprise',
        phone: 'Téléphone *',
        vatNumber: 'TVA (optionnel)',
        street: 'Rue & n° *',
        postalCode: 'Code postal *',
        city: 'Ville *',
        country: 'Pays *',
        profileButton: 'Enregistrer le profil',
        currentPassword: 'Mot de passe actuel',
        newPassword: 'Nouveau mot de passe',
        confirmPassword: 'Confirmer le mot de passe',
        passwordButton: 'Mettre à jour le mot de passe',
        deleteButton: 'Supprimer le compte',
      },
      releaseNotes: {
        title: 'Notes de version',
        subtitle: 'Toutes les nouveautés en un coup d’œil.',
        empty: 'Aucune note de version.',
        loading: 'Chargement des notes de version…',
        loadError: 'Impossible de charger les notes de version.',
        fallbackTitle: 'Version',
      },
      plan: {
        unknown: 'Plan inconnu.',
        togglePicker: 'Changer de plan',
        noAlternatives: 'Aucun autre plan disponible.',
        alternativesTitle: 'Plans disponibles',
        switchButton: 'Changer',
        currentBadge: 'Plan actuel',
        pendingLabel: 'Changement vers "{{plan}}" en attente.',
        notices: {
          pending: {
            title: 'Changement en attente',
            description: 'Le passage à "{{plan}}" n’est pas terminé. Finalise le paiement pour activer le plan.',
            action: 'Reprendre le paiement',
          },
          payment: {
            title: 'Moyen de paiement requis',
            description: 'Le plan "{{plan}}" nécessite un moyen de paiement actif. Ajoute-le pour débloquer les uploads.',
            action: 'Ajouter un moyen de paiement',
          },
        },
        billingFallback: 'Carte',
        billingMissing: 'Aucun moyen de paiement enregistré.',
      },
      statuses: {
        planAlreadyActive: 'Ce plan est déjà actif.',
        unknownPlan: 'Plan inconnu.',
        openingCheckout: 'Ouverture de Stripe Checkout…',
        updatingPlan: 'Mise à jour du plan…',
        redirectingStripe: 'Redirection vers Stripe…',
        planUpdated: 'Plan mis à jour.',
        planFailed: 'Le changement de plan a échoué.',
        profileSaving: 'Enregistrement…',
        profileSaved: 'Profil enregistré.',
        profileError: 'Le profil n’a pas pu être enregistré.',
        passwordMissingCurrent: 'Le mot de passe actuel est requis.',
        passwordTooShort: 'Le nouveau mot de passe doit contenir au moins 6 caractères.',
        passwordMismatch: 'Le nouveau mot de passe et sa confirmation diffèrent.',
        passwordUpdating: 'Mise à jour du mot de passe…',
        passwordUpdated: 'Mot de passe mis à jour.',
        passwordError: 'Le mot de passe n’a pas pu être mis à jour.',
        deleteConfirm: 'Supprimer définitivement ton compte ? Action irréversible.',
        deleteInProgress: 'Suppression du compte…',
        deleteSuccess: 'Compte supprimé. Tu vas être déconnecté.',
        deleteFailed: 'Le compte n’a pas pu être supprimé.',
      },
    },
    auth: {
      titles: {
        login: 'Connexion',
        register: 'Créer un compte',
      },
      sections: {
        profile: 'Données personnelles',
      },
      descriptions: {
        login: 'Connecte-toi avec ton e-mail pour accéder à tes uploads.',
        register:
          'Crée un nouveau compte. Les mots de passe sont stockés chiffrés. Tu pourras choisir un plan payant plus tard.',
      },
      requiredNote: '* Champs requis',
      labels: {
        email: 'E-mail *',
        password: 'Mot de passe *',
        firstName: 'Prénom *',
        lastName: 'Nom *',
        company: 'Entreprise (optionnel)',
        phone: 'Téléphone *',
        vatNumber: 'TVA (optionnel)',
        street: 'Rue & n° *',
        postalCode: 'Code postal *',
        city: 'Ville *',
        country: 'Pays *',
      },
      placeholders: {
        company: 'Organisation',
        phone: '+33 6 12 34 56 78',
        vatNumber: 'FR123456789',
        street: '10 Rue Exemple',
        postalCode: '75000',
        city: 'Ville',
        country: 'France',
      },
      planSelector: {
        title: 'Choisir un plan',
        subtitle: 'Sélectionne le plan adapté. Tu pourras le changer ensuite.',
        selectButton: 'Choisir le plan',
        selectedButton: 'Sélectionné',
        currentLabel: 'Plan actuel : {{plan}}',
        unknownPlan: 'Inconnu',
      },
      buttons: {
        submit: {
          login: 'Connexion',
          register: 'Inscription',
        },
      },
      toggle: {
        login: {
          question: 'Pas encore de compte ?',
          action: 'Créer un compte',
        },
        register: {
          question: 'Déjà inscrit ?',
          action: 'Aller au login',
        },
      },
      forgotLink: 'Mot de passe oublié ?',
      forgot: {
        title: 'Mot de passe oublié',
        subtitle: 'Saisis ton e-mail et nous t’enverrons un lien de réinitialisation.',
        emailLabel: 'Adresse e-mail',
        submit: 'Envoyer le lien',
        sending: 'Envoi du lien…',
        sent: 'Si un compte existe, un e-mail de réinitialisation a été envoyé.',
        error: 'La demande a échoué.',
        backToLogin: 'Retour à la connexion',
      },
      reset: {
        title: 'Réinitialiser le mot de passe',
        subtitle: 'Définis un nouveau mot de passe pour ton compte.',
        emailLabel: 'Adresse e-mail',
        passwordLabel: 'Nouveau mot de passe',
        confirmLabel: 'Confirmer le mot de passe',
        submit: 'Enregistrer',
        saving: 'Enregistrement…',
        success: 'Mot de passe mis à jour. Tu peux te connecter.',
        error: 'Le mot de passe n’a pas pu être mis à jour.',
        missingToken: 'Le lien est invalide ou manquant.',
        backToRequest: 'Demander un nouveau lien',
        backToLogin: 'Retour à la connexion',
        passwordTooShort: 'Le mot de passe doit contenir au moins 6 caractères.',
        passwordMismatch: 'Les mots de passe ne correspondent pas.',
      },
      statuses: {
        checkingCredentials: 'Vérification des accès…',
        creatingAccount: 'Création du compte…',
        successRedirect: 'Succès. Redirection…',
        genericError: 'Action échouée.',
        redirectingCheckout: 'Redirection vers le paiement…',
        checkoutMissingUrl: 'URL de paiement manquante.',
        checkoutErrorPrefix: 'Erreur de paiement :',
      },
    },
    admin: {
      title: 'Console admin',
      description: 'Vue d’ensemble de tous les comptes enregistrés. Les modifications sont appliquées immédiatement (store démo local).',
      toggle: { open: 'Nouvel utilisateur', close: 'Annuler' },
      loading: 'Chargement des utilisateurs…',
      empty: 'Aucun utilisateur pour le moment.',
      addUser: { title: 'Créer un utilisateur', submit: 'Créer un utilisateur' },
      form: {
        firstName: 'Prénom',
        lastName: 'Nom',
        email: 'E-mail',
        password: 'Mot de passe',
        company: 'Entreprise',
        phone: 'Téléphone',
        vat: 'TVA (optionnel)',
        street: 'Rue & n°',
        postalCode: 'Code postal',
        city: 'Ville',
        country: 'Pays',
        plan: 'Plan',
        role: 'Rôle',
        uiVersion: 'Version UI',
        newPassword: 'Nouveau mot de passe',
        confirmPassword: 'Confirmer le mot de passe',
      },
      placeholders: {
        company: 'Organisation',
        phone: '+33 6 12 34 56 78',
        vat: 'FR123456789',
        street: '10 Rue Exemple',
        postalCode: '75000',
        city: 'Ville',
        country: 'France',
      },
      table: {
        headers: {
          profile: 'Profil',
          registered: 'Inscription',
          plan: 'Plan',
          role: 'Rôle',
          uiVersion: 'Version UI',
          stripe: 'Stripe',
          jobsCurrentMonth: 'Jobs (mois)',
          jobsTotal: 'Jobs (total)',
          jobs: 'Jobs',
          jobsCurrentMonthSub: 'par mois',
          jobsTotalSub: 'total',
          actions: 'Actions',
        },
        profileFallback: '—',
        editButton: 'Modifier',
      },
      stripe: { none: '—', open: 'ouvrir' },
      jobStats: {
        title: 'Jobs par mois',
        columns: { month: 'Mois', count: 'Jobs' },
        empty: 'Aucun job enregistré.',
      },
      plan: { unknown: 'Plan inconnu' },
      roles: { admin: 'Admin', user: 'User' },
      uiVersion: {
        optionLabel: 'Version {{version}}',
        badgeLabel: 'V{{version}}',
        invalid: 'Version UI invalide.',
      },
      actions: { close: 'Fermer', save: 'Enregistrer', cancel: 'Annuler', delete: 'Supprimer le compte' },
      modal: { title: 'Modifier l’utilisateur' },
      statuses: {
        createRequired: 'E-mail et mot de passe sont requis.',
        createSuccess: 'Utilisateur {{email}} créé.',
        createFailed: 'Création impossible.',
        passwordTooShort: 'Le nouveau mot de passe doit contenir au moins 6 caractères.',
        passwordMismatch: 'Le nouveau mot de passe et sa confirmation diffèrent.',
        updateSaving: 'Enregistrement des modifications…',
        updateSuccess: 'Modifications enregistrées pour {{email}}.',
        updateFailed: 'Mise à jour impossible.',
        deleteConfirm: 'Supprimer ce compte ?',
        deleteInProgress: 'Archivage du compte…',
        deleteSuccess: 'Compte {{email}} archivé.',
        deleteFailed: 'Échec de l’archivage.',
        loadFailed: 'Impossible de charger les utilisateurs.',
      },
    },
    live: {
      title: 'Transcription live',
      buttons: {
        start: 'Démarrer la session',
        stop: 'Arrêter la session',
        settingsOpen: 'Paramètres',
        settingsClose: 'Masquer les paramètres',
        transcriptShow: 'Afficher le transcript live',
        transcriptHide: 'Masquer le transcript live',
        share: 'Partager le lien',
      },
      fields: {
        customerLabel: 'Client (optionnel)',
        customerPlaceholder: 'Client',
        projectLabel: 'Projet (optionnel)',
        projectPlaceholder: 'Projet',
        meetingLabel: 'Date de réunion (optionnel)',
      },
      domainLabel: 'Domaine',
      domainOptions: [
        { value: 'enterprise-software', label: "Logiciel d'entreprise" },
        { value: 'bau', label: 'Construction' },
        { value: 'maschinenbau', label: 'Génie mécanique' },
        { value: 'produktentwicklung', label: 'Développement produit' },
        { value: 'sonstiges', label: 'Autre' },
      ],
      domainDefault: 'enterprise-software',
      info: {
        engineLabel: 'Moteur',
        languageLabel: 'Langue',
        languageUnknown: 'Pas encore détectée.',
        languageManualSuffix: ' (manuel)',
        statusReady: 'Prêt. En attente de démarrage.',
        extractionWarning:
          'Les exigences n’ont pas pu être entièrement validées. Derniers résultats connus affichés.',
        footer:
          'Lance une session live : micro et audio système sont diffusés en parallèle et transcrits en temps réel. Merci d’autoriser le navigateur pour les deux sources.',
      },
      audio: {
        systemLabel: 'Système',
        micLabel: 'Micro',
        sourceSystem: 'Audio système actif',
        sourceMic: 'Micro actif',
      },
      audioModes: [
        { code: 'mix', label: 'Système + micro', description: 'Capturer l’audio système et le micro en parallèle' },
      ],
      requirements: {
        title: 'Exigences détectées',
        empty: 'Aucune exigence pour le moment.',
        fallbackType: 'Autre',
        shareMissing: 'Aucune exigence disponible pour l’instant.',
        shareCopied: 'Lien copié !',
        shareCreateError: 'Impossible de créer le lien.',
        shareClipboardError: 'Échec de copie. <a href="{{url}}" target="_blank">Ouvrir le lien</a>',
      },
      transcript: {
        empty: 'Aucun audio reçu pour le moment.',
      },
      statuses: {
        settingsLockedLanguage: 'Session active – langue verrouillée.',
        settingsLockedEngine: 'Session active – moteur non modifiable.',
        settingsLockedAudio: 'Session active – source audio verrouillée.',
        settingsLockedDomain: 'Session active – domaine verrouillé.',
        connecting: 'Connexion…',
        systemAudioUnsupported: 'Audio système indisponible – enregistrement micro uniquement.',
        chunkBuffered: 'Fragment audio incomplet – attente de données…',
        chunkSkipped: 'Fragment audio ignoré – incomplet.',
        sessionStreaming: 'Session active – streaming en cours.',
        chunkSendFailed: 'Envoi échoué. Nouvel essai…',
        waitingAudio: 'Session active – attente des premiers sons…',
        waitingAudioMic: 'Session active – attente des premiers sons (micro seul).',
        stopError: 'Impossible d’arrêter la session.',
        startError: 'La session n’a pas pu démarrer.',
      },
      appFactory: {
        title: 'App Factory',
        description: 'Conduis des entretiens structurés depuis la vue live.',
        sessionSelectLabel: 'Sélectionner une session',
        sessionSelectPlaceholder: 'Choisir une session…',
        sessionEmpty: 'Aucune session App Factory.',
      buttons: {
        refresh: 'Actualiser',
        newSession: 'Nouvelle session',
        analyze: 'Analyser le progrès',
        sync: 'Synchroniser les exigences',
        open: 'Ouvrir la session',
      },
      sessionLabel: 'Session',
      customerLabelShort: 'Client',
      projectLabelShort: 'Projet',
      autoSyncLabel: 'Synchroniser automatiquement les exigences',
        requirementsTitle: 'Exigences live',
        requirementsHint: 'Choisis une session pour synchroniser.',
        requirementsSummary: '{{count}} exigences prêtes.',
        guidelineEmpty: 'Aucun guide chargé.',
        status: {
          selectSession: 'Sélectionne une session.',
          loadingSessions: 'Chargement des sessions…',
          loadingSession: 'Chargement du guide…',
          syncEmpty: 'Aucune exigence pour le moment.',
          syncing: 'Synchronisation des exigences…',
          syncSuccess: 'Exigences synchronisées.',
          analyzing: 'Analyse en cours…',
          updatedAt: 'Mis à jour à {{time}}',
        },
        errors: {
          load: 'Impossible de charger les sessions.',
          create: 'Création de la session impossible.',
          analyze: 'Analyse impossible.',
          sync: 'Synchronisation impossible.',
          session: 'Impossible de charger la session.',
          init: 'App Factory n’a pas pu démarrer.',
        },
        prompts: {
          customer: 'Optionnel : saisir l’ID client',
          project: 'Optionnel : saisir l’ID projet',
        },
        creating: 'Création de la session…',
      },
    },
    liveShare: {
      title: 'Exigences partagées',
      loadError: 'Lien illisible. Merci de le redemander.',
      sessionLabel: 'Session du {{date}}',
      sharedBy: {
        prefix: 'Partagé par',
        unknown: 'inconnu',
        companySeparator: ' – ',
      },
      buttons: {
        csv: 'Télécharger CSV',
        md: 'Télécharger Markdown',
      },
      empty: 'Aucune exigence partagée.',
      note: 'Note : cette vue montre un instantané de la session live. Les mises à jour temps réel arriveront avec le futur backend.',
      card: {
        fallbackType: 'Autre',
      },
      download: {
        csvHeaders: {
          type: 'Type',
          text: 'Texte',
        },
        markdown: {
          title: 'Exigences détectées',
          sessionLabel: 'Session',
        },
      },
    },
    conversation: {
      title: 'Conversation',
      loading: 'Chargement des résultats…',
      backToJobs: 'Retour aux jobs',
      backLabel: 'Retour',
      header: {
        status: 'Statut',
        conversationId: 'ID de conversation',
        customer: 'Client',
        project: 'Projet',
        meeting: 'Réunion',
        created: 'Créé',
        updated: 'Mis à jour',
      },
      progress: {
        label: 'Statut',
        phases: {
          uploaded: 'Téléversé',
          processing: 'Traitement',
          transcribing: 'Transcription en cours',
          extracting: 'Extraction des événements',
          live: 'Session live active',
          finalizing: 'Finalisation',
          finalized: 'Terminé',
          failed: 'Échec',
          unknown: 'Inconnu',
        },
      },
      delete: {
        button: 'Supprimer le job',
        confirm: 'Supprimer ce job ? Toutes les exigences, transcripts et fichiers seront supprimés.',
        inProgress: 'Suppression…',
        success: 'Job supprimé.',
        error: 'Le job n’a pas pu être supprimé.',
      },
      metaEdit: {
        button: 'Modifier',
        save: 'Enregistrer',
        cancel: 'Annuler',
        inProgress: 'Enregistrement…',
        error: 'La mise à jour a échoué.',
      },
      filters: {
        topics: 'Thèmes',
        topicsAll: 'Tous les thèmes',
        noTopics: 'Aucun thème disponible.',
        contentLabel: 'Afficher',
        contentAll: 'Tout',
        priorityLabel: 'Priorité',
        priorityAll: 'Toutes les priorités',
        categoryLabel: 'Catégorie',
        categoryAll: 'Toutes les catégories',
        searchLabel: 'Recherche',
        searchPlaceholder: 'Texte, ID ou mot-clé',
        clear: 'Réinitialiser les filtres',
        countLabel: '{{count}} résultats',
        noResults: 'Aucun élément ne correspond à tes filtres.',
        emptyAll: 'Aucune exigence ni user story pour l’instant.',
        priorityOptions: {
          high: 'Haute',
          medium: 'Moyenne',
          low: 'Faible',
          must: 'Must',
          should: 'Should',
          could: 'Could',
          wont: "Won't",
        },
        topicLabels: {
          functional: 'Fonctionnel',
          non_functional: 'Non fonctionnel',
          business: 'Business',
          knowledge_management: 'Gestion des connaissances',
          system_integration: 'Intégration système',
          lifecycle_tracking: 'Suivi du cycle de vie',
          onboarding_enablement: 'Activation onboarding',
          global_enablement: 'Activation globale',
          data_governance: 'Gouvernance des données',
          analytics_automation: 'Automatisation analytique',
          service_quality: 'Qualité de service',
          other: 'Autre',
        },
      },
      sections: {
        requirements: 'Exigences',
        userStories: 'User stories',
        deferredDecisions: 'Décisions reportées',
      },
      summary: {
        title: 'Executive Summary',
        download: 'Télécharger le résumé',
        downloadDescription: 'Executive Summary (Markdown)',
        collapse: 'Réduire',
        expand: 'Développer',
        overview: 'Vue d’ensemble',
        keyRequirements: 'Exigences clés',
        openDecisions: 'Décisions ouvertes',
        risks: 'Risques',
        nextSteps: 'Prochaines étapes',
        downloadSuccess: 'Téléchargement de l’Executive Summary démarré.',
        downloadError: 'L’Executive Summary n’a pas pu être téléchargé.',
      },
      exports: {
        csv: 'Export CSV',
        md: 'Export Markdown',
      },
      requirements: {
        empty: 'Aucune exigence pour le moment.',
        rationale: 'Justification',
        source: 'Source',
        speaker: 'Intervenant',
        tags: 'Tags',
      },
      edit: {
        editButton: 'Modifier',
        copyButton: 'Copier',
        copySuccess: 'Copié.',
        copyError: 'Copie échouée.',
        saveButton: 'Enregistrer',
        cancelButton: 'Annuler',
        priorityUnset: 'Sans priorité',
        fields: {
          text: 'Texte',
          story: 'User story',
          role: 'Rôle',
          goal: 'Objectif',
          benefit: 'Bénéfice',
          acceptance: 'Critères d’acceptation',
          category: 'Catégorie',
          priority: 'Priorité',
          topics: 'Thèmes',
        },
        topicsPlaceholder: 'ex. Sécurité, Reporting',
      },
      userStories: {
        empty: 'Aucune user story pour le moment.',
        role: 'Rôle',
        goal: 'Objectif',
        benefit: 'Bénéfice',
        acceptance: 'Critères d’acceptation',
        priorityFallback: 'Medium',
      },
      log: {
        title: 'Journal des modifications',
        empty: 'Aucune modification.',
        note: 'Les modifications sont enregistrées dans la base de données.',
        emptyValue: '—',
      },
    },
    qaChat: {
      title: 'Chat Q&R avec sources',
      description: 'Posez des questions sur les exigences et les transcriptions. Les réponses incluent des preuves traçables.',
      requirementsTitle: 'Q&R sur toutes les exigences',
      requirementsDescription: 'Les filtres actuels servent de contexte (client, projet, période).',
      conversationTitle: 'Q&R de la conversation',
      conversationDescription: 'Posez des questions ciblées sur cette conversation avec sources, intervenant et temps.',
      roles: {
        user: 'Vous',
        assistant: 'Assistant',
      },
      state: {
        loading: 'Chargement du chat…',
        empty: 'Aucun message pour le moment. Posez votre première question.',
      },
      input: {
        placeholder: 'Exemple : De quelle couleur doit être la maison ?',
      },
      actions: {
        send: 'Envoyer',
        newThread: 'Nouveau fil',
        deleteThread: 'Supprimer',
        includeGeneralAnswer: '+ Savoir général (Test)',
        searchGlobal: 'Rechercher globalement',
      },
      labels: {
        generalAnswer: 'Savoir général (Test)',
      },
      feedback: {
        threadDeleted: 'Fil supprimé.',
      },
      citations: {
        title: 'Sources',
        unknownSpeaker: 'Intervenant inconnu',
        unknownCustomer: 'Client inconnu',
        unknownProject: 'Projet inconnu',
      },
      errors: {
        default: 'Échec de la requête.',
        send: 'Le message n’a pas pu être envoyé.',
        loadThread: 'Le fil n’a pas pu être chargé.',
        createThread: 'Le fil n’a pas pu être créé.',
        deleteThread: 'Le fil n’a pas pu être supprimé.',
        loadThreads: 'Les fils n’ont pas pu être chargés.',
      },
    },
    imprint: {
      title: 'Mentions légales',
      intro: "Mentions légales applicables au service AnforderungsAI disponible sur https://anforderungsai.de.",
      tabs: {
        imprint: 'Mentions légales',
        agb: 'Conditions',
        privacy: 'Confidentialité',
      },
      provider: {
        heading: 'Éditeur',
        nameLabel: 'Nom',
        name: IMPRINT_DETAILS.name,
        addressLabel: 'Adresse',
        addressLines: [IMPRINT_DETAILS.street, IMPRINT_DETAILS.postalCodeCity, IMPRINT_DETAILS.country],
        registerLabel: 'Inscription au registre',
        registerNote: 'Aucune inscription au registre du commerce.',
      },
      contact: {
        heading: 'Contact',
        emailLabel: 'E-mail',
        email: IMPRINT_DETAILS.email,
        phoneLabel: 'Téléphone',
        phone: 'Non publié – merci de prendre contact par e-mail.',
      },
      vat: {
        heading: 'ID TVA',
        text: `Numéro de TVA intracommunautaire (art. 27a UStG) : ${IMPRINT_DETAILS.taxId}`,
      },
      supervision: {
        heading: 'Autorité de supervision',
        text: "Aucune autorité de supervision spécifique n'est compétente.",
      },
      profession: {
        heading: 'Règles professionnelles',
        text: 'Aucune profession réglementée ; aucune obligation professionnelle spécifique.',
      },
      dispute: {
        heading: 'Règlement des litiges / Plateforme ODR',
        text: 'La Commission européenne propose une plateforme de règlement en ligne des litiges (ODR) :',
        platformLabel: 'https://ec.europa.eu/consumers/odr',
        participation: "Nous ne sommes ni tenus ni disposés à participer à une procédure de médiation pour consommateurs.",
      },
      editorial: {
        heading: 'Responsable éditorial (art. 18 al. 2 MStV)',
        text: `${IMPRINT_DETAILS.name}, même adresse.`,
      },
      notes: {
        lastUpdated: 'Mise à jour : janvier 2025',
      },
    },
  },
  es: {
    common: {
      loading: 'Cargando…',
      noData: 'Todavía no hay datos.',
      error: 'No se pudieron cargar los datos.',
      authRequired: 'Inicia sesión para ver estos datos.',
      download: {
        button: 'Descargar',
        title: 'Descargar',
        subtitle: 'Elige un formato y comienza la exportación.',
        formatLabel: 'Formato',
        templateLabel: 'Plantilla',
        standardOption: 'Exportación estándar',
        manageTemplates: 'Gestionar plantillas',
        noTemplates: 'No hay plantillas disponibles.',
        cancel: 'Cancelar',
        confirm: 'Iniciar descarga',
        loading: 'Descargando…',
      },
    },
    nav: {
      home: 'Inicio',
      dashboard: 'Panel',
      requirements: 'Requisitos',
      customersProjects: 'Clientes / Proyectos',
      upload: 'Subidas',
      newRequirements: 'Nuevo evento',
      jobs: 'Jobs',
      live: 'Live',
      appFactory: 'App Factory',
      profile: 'Cuenta',
      admin: 'Admin',
      imprint: 'Aviso legal',
      login: 'Login',
      register: 'Registro',
    },
    sidebar: {
      tagline: 'Cabina de requisitos de nueva generación',
      navigationLabel: 'Navegación',
      accessLabel: 'Acceso',
      themeLabel: 'Tema',
      languageLabel: 'Idioma',
      loggedInLabel: 'Conectado como',
      logout: 'Cerrar sesión',
      logoutDemo: 'Salir de la demo',
      menuOpen: 'Abrir menú',
      menuClose: 'Cerrar menú',
      languages: createLanguageLabels(),
    },
    releaseNotes: {
      title: 'Novedades',
      subtitle: 'Cambios desde tu último inicio de sesión.',
      confirmButton: 'Entendido',
      ackInProgress: 'Guardando confirmación…',
      ackError: 'La confirmación falló.',
      fallbackTitle: 'Release',
    },
    home: {
      heroEyebrow: 'Gestión de requisitos para equipos de producto y proyecto',
      heroTitle: 'De la conversación a requisitos claros en minutos.',
      heroDescription: `AnforderungsAI extrae requisitos de una amplia variedad de fuentes — por ejemplo, entrevistas en vivo, talleres y videollamadas, así como documentos, transcripciones y archivos de audio y vídeo.
Los contenidos se estructuran, contextualizan y priorizan automáticamente y luego quedan disponibles en varios formatos para su procesamiento y exportación.`,
      heroMore:
        'La pipeline transcribe audio y vídeo, identifica afirmaciones relevantes y las vincula a metadatos. El resultado es un catálogo de requisitos trazable que ayuda a revisar, priorizar y entregar.',
      heroToggleMore: 'Detalles del producto',
      heroToggleLess: 'Ocultar detalles',
      ctas: {
        primary: 'Solicitar demo',
        secondary: 'Ver demo',
        pricing: 'Ver precios',
        register: 'Solicitar demo',
      },
      trust: [
        'Orientado a GDPR',
        'Hosting en la UE',
        'CSV, Markdown y JSON',
        'Live o upload',
      ],
      featuresTitle: 'Lo que entrega AnforderungsAI',
      featuresSubtitle: 'Para equipos que quieren convertir conversaciones en requisitos utilizables.',
      features: [
        {
          title: 'Workflows en vivo y archivos',
          description: 'Inicia una sesión en vivo o sube audio, vídeo o un transcript.',
        },
        {
          title: 'Extracción con contexto',
          description: 'Cada requisito se mantiene trazable con fuente, justificación y tags.',
        },
        {
          title: 'Estructura y priorización',
          description: 'Tipos, estados y prioridades quedan limpios y filtrables.',
        },
        {
          title: 'Resultados listos para exportar',
          description: 'CSV, Markdown o JSON con plantillas — listo para doc y backlog.',
        },
        {
          title: 'Compartir en equipo',
          description: 'Los resultados se comparten por enlace y se revisan en conjunto.',
        },
        {
          title: 'Protección de datos en la UE',
          description: 'Procesamiento y almacenamiento pensados para requisitos europeos.',
        },
      ],
      stepsTitle: 'Cómo funciona',
      stepsSubtitle: 'En tres pasos claros desde la fuente hasta el resultado utilizable.',
      steps: [
        {
          title: 'Elegir una fuente',
          description: 'Selecciona entrevista, workshop o documento existente.',
        },
        {
          title: 'El análisis se ejecuta',
          description: 'Transcripción y extracción generan requisitos con contexto.',
        },
        {
          title: 'Entregar resultados',
          description: 'Revisar, priorizar y exportar para la entrega.',
        },
      ],
      demo: {
        title: 'Probar ahora',
        description:
          'Sube un archivo de texto, CSV o JSON. Mostramos un requisito detectado como vista previa. Para el análisis completo necesitas login.',
        button: 'Verificar archivo',
        note: 'La demo se procesa localmente en tu navegador. No se sube nada.',
        error: 'Se produjo un error al leer el archivo.',
        noRequirement: 'No se detectó ningún requisito.',
        downloadLabel: 'Descargar archivo de ejemplo',
        fileLabel: 'Seleccionar archivo',
        formatsHint: 'Formatos: TXT, MD, CSV, JSON.',
        customerLabel: 'Cliente',
        projectLabel: 'Proyecto',
        snippetLabel: 'Extracto',
        successTitle: 'Resultado de ejemplo',
        successNote: 'Para resultados completos, inicia sesión.',
        selectFileWarning: 'Selecciona un archivo.',
        loading: 'Analizando archivo…',
      },
      demoFile: {
        customer: 'Nordachse Mobility',
        project: 'Hub de carga UE',
        snippet: 'El hub de carga debe soportar estaciones modulares y reaccionar al pico en menos de 2 minutos.',
      },
      pricingTitle: 'Precios',
      pricingSubtitle: 'Facturación transparente, ajustable en cualquier momento. Pagos vía Stripe.',
      pricingNote: 'Para piloto o enterprise ofrecemos paquetes a medida.',
      pricingDetails: {
        quota: {
          monthly: 'Uploads por mes incluidos',
          per_job: 'Facturación flexible por upload procesado',
          unlimited: 'Uploads ilimitados con procesamiento prioritario',
          default: 'Uso flexible según tus necesidades',
        },
        billingRequired: 'Se requieren datos de pago',
        free: 'Empieza gratis',
        actionLabel: 'Elegir plan',
        unitPriceLabel: '€3 / Nutzung',
      },
      testimonialsTitle: 'Voces de proyectos',
      testimonialsSubtitle:
        'Equipos de producto, proyecto y consultoría usan AnforderungsAI para capturar requisitos de forma consistente y entregar más rápido.',
      testimonials: [
        {
          quote: 'Recibimos requisitos con fuente y ahorramos retrabajo en las revisiones.',
          author: 'Product manager, B2B SaaS',
        },
        {
          quote: 'Las plantillas de exportación nos dan un formato limpio para doc y backlog.',
          author: 'Scrum master, agencia digital',
        },
        {
          quote: 'Especialmente al inicio, obtenemos estructura rápida en el feedback de stakeholders.',
          author: 'Líder de proyecto IT, mediana empresa',
        },
      ],
      contactButton: 'Solicitar demo',
      demoButton: 'Ver demo',
      faqTitle: 'Preguntas frecuentes',
      faqSubtitle: 'Respuestas cortas para facilitar la decisión.',
      faq: [
        {
          question: '¿Qué formatos están soportados?',
          answer: 'Audio/vídeo (MP3, MP4, WAV, QTA) y transcripts (TXT, MD, JSON, CSV, VTT, SRT).',
        },
        {
          question: '¿Qué pasa con mis datos?',
          answer: 'El archivo de demo se procesa localmente en el navegador. Para el análisis completo se sube y procesa en tu workspace.',
        },
        {
          question: '¿Cómo se ven los resultados?',
          answer: 'Requisitos estructurados con fuente, tags y user stories opcionales. Exportación CSV, Markdown o JSON.',
        },
        {
          question: '¿Qué tan rápido obtengo resultados?',
          answer: 'Normalmente en pocos minutos, según la longitud y el formato de la fuente.',
        },
      ],
      ctaTitle: 'Aporta claridad a tus requisitos',
      ctaSubtitle: 'Agenda una demo o prueba la extracción con tu propio archivo.',
      ctaPrimary: 'Solicitar demo',
      ctaSecondary: 'Ver demo',
    },
    upload: {
      title: 'Subir archivo',
      formatsHint: 'Formatos admitidos: MP3, MP4, WAV, QTA.',
      description: 'Formatos: MP3, MP4, WAV, QTA. Tras el upload, Transcribe se inicia automáticamente.',
      fileLabel: 'Archivo de audio o vídeo',
      engineLabel: 'Motor de transcripción',
      engineDescription: 'Elige el motor de reconocimiento de voz.',
      engines: {
        mistral: 'Mistral (Voxtral)',
        aws: 'AWS Transcribe',
      },
      customerLabel: 'Cliente (opcional)',
      projectLabel: 'Proyecto (opcional)',
      meetingLabel: 'Fecha de reunión (opcional)',
      customerPlaceholder: 'ID de cliente',
      projectPlaceholder: 'Nombre del proyecto',
      domainLabel: 'Dominio',
      domainOptions: [
        { value: 'enterprise-software', label: 'Software empresarial' },
        { value: 'bau', label: 'Construcción' },
        { value: 'maschinenbau', label: 'Ingeniería mecánica' },
        { value: 'produktentwicklung', label: 'Desarrollo de producto' },
        { value: 'sonstiges', label: 'Otro' },
      ],
      domainDefault: 'enterprise-software',
      button: 'Iniciar subida',
      selectFileWarning: 'Selecciona un archivo.',
      creatingLink: 'Creando enlace…',
      uploading: 'Subiendo archivo…',
      uploadDone: 'Upload completado.',
      uploadError: 'El upload ha fallado.',
      planNote: 'Plan actual:',
      planNoteCta: 'Gestionar plan',
      listTitle: 'Uploads recientes',
      progressLabel: 'Progreso',
      successTitle: 'Upload exitoso',
      conversationLabel: 'ID de conversación',
      viewJobs: 'Ver jobs',
      copyId: 'Copiar ID',
      copySuccess: 'Copiado',
      copyError: 'Error al copiar',
      successNote: 'El procesamiento continúa en segundo plano. Consulta la vista de Jobs.',
      statuses: {
        running: 'En proceso',
        success: 'Listo para exportar',
        error: 'Error',
      },
      transcript: {
        title: 'Subir transcript',
        description: '¿Ya tienes un transcript en texto o JSON? Súbelo y evita la transcripción automática.',
        fileLabel: 'Archivo de transcript',
        formatsHint: 'Formatos admitidos: TXT, MD, JSON, CSV, VTT, SRT, PDF, DOCX.',
        button: 'Procesar transcript',
        successTitle: 'Transcript recibido',
        successNote: 'La extracción comienza de inmediato usando tu transcript.',
        status: {
          readingPdf: 'Leyendo PDF…',
          pdfEmpty: 'El PDF no contiene texto legible.',
          readingDocx: 'Leyendo documento Word…',
          docxEmpty: 'El documento Word no contiene texto legible.',
          docUnsupported: 'El formato .doc no es compatible. Guarda como .docx.',
          finalizing: 'Iniciando extracción…',
          uploadError: 'El upload del transcript falló.',
        },
      },
    },
    newRequirements: {
      title: 'Nuevos requisitos',
      subtitle: 'Inicia una sesión en vivo o sube archivos/transcripts para crear requisitos.',
      uploadTitle: 'Subir audio / vídeo',
      live: {
        badge: 'Live',
        title: 'Transcripción en vivo',
        description: 'Captura conversaciones en tiempo real y extrae requisitos al instante.',
        note: 'Audio de micrófono o sistema transmitido en vivo.',
      },
    },
    requirementsList: {
      title: 'Eventos',
      subtitle: 'Todos los eventos por cliente, proyecto y periodo.',
      summary: {
        total: 'Total',
      },
      actions: {
        refresh: 'Actualizar',
        exportCsv: 'Exportar CSV',
        exportMd: 'Exportar Markdown',
        exportJson: 'Exportar JSON',
        exportXlsx: 'Exportar Excel',
        exportDocx: 'Exportar Word',
        download: 'Descargar',
      },
      filters: {
        search: 'Buscar',
        searchPlaceholder: 'Texto, tags, categorías...',
        customer: 'Cliente',
        customerPlaceholder: 'ID de cliente',
        project: 'Proyecto',
        projectPlaceholder: 'Nombre del proyecto',
        from: 'Desde',
        to: 'Hasta',
        status: 'Estado',
        statusAll: 'Todos los estados',
        customerAll: 'Todos los clientes',
        projectAll: 'Todos los proyectos',
        none: 'Ninguno',
        priority: 'Prioridad',
        priorityAll: 'Todas las prioridades',
        type: 'Tipo',
        typeAll: 'Todos los tipos',
        reset: 'Restablecer filtros',
      },
      status: {
        NEW: 'Nuevo',
        OPEN: 'Abierto',
        BACKLOG: 'Backlog',
        IN_PROGRESS: 'En progreso',
        DONE: 'Hecho',
        ARCHIVED: 'Archivado',
      },
      priorities: {
        must: 'Debe',
        should: 'Debería',
        could: 'Podría',
        wont: 'No',
      },
      types: {
        aufgabe: 'Tarea',
        termin: 'Fecha',
        entscheidung: 'Decisión',
        anforderung: 'Requisito',
        frage: 'Pregunta',
        risiko: 'Riesgo',
        annahme: 'Suposición',
        deferred: 'Aplazamiento',
        sonstiges: 'Otro',
        blocker: 'Bloqueador',
        dependency: 'Dependencia',
        functional: 'Funcional',
        nonfunctional: 'No funcional',
        constraint: 'Restricción',
        risk: 'Riesgo',
        assumption: 'Supuesto',
        open_question: 'Pregunta abierta',
      },
      table: {
        text: 'Evento',
        customer: 'Cliente/Proyecto',
        date: 'Fecha',
        type: 'Tipo',
        priority: 'Prioridad',
        status: 'Estado',
        actions: 'Acción',
        open: 'Abrir',
        copyLabel: 'Copiar',
        copySuccess: 'Copiado.',
        copyError: 'Error al copiar.',
      },
      empty: 'No se encontraron eventos.',
      errors: {
        load: 'No se pudieron cargar los eventos.',
        save: 'No se pudo guardar el estado.',
      },
      toast: {
        statusSaved: 'Estado actualizado.',
      },
    },
    customersProjects: {
      title: 'Clientes / Proyectos',
      description: 'Selecciona un cliente y un proyecto para ver un resumen automático y Q&A contextual.',
      panes: {
        customers: 'Clientes',
        projects: 'Proyectos',
      },
      labels: {
        projects: 'Proyectos',
        conversations: 'Conversaciones',
        lastUpdate: 'Actualizado',
        whatItDoes: '¿Qué hace este proyecto?',
        whatItIsAbout: '¿De qué trata?',
        nextSteps: '¿Qué sigue?',
        highlights: 'Aspectos clave',
        fallback: 'Fallback',
        sourceStats: 'Fuentes: {{used}} / {{total}} conversaciones analizadas',
      },
      states: {
        indexLoading: 'Cargando clientes y proyectos…',
        emptyTitle: 'Todavía no hay datos',
        emptyDescription: 'Inicia una subida o una sesión en vivo.',
        noProjects: 'Aún no hay proyectos para este cliente.',
        selectProject: 'Selecciona un proyecto.',
        selectProjectForQa: 'Q&A se activa en cuanto selecciones un proyecto.',
        summaryLoading: 'Generando resumen…',
        summaryChecking: 'Comprobando actualizaciones…',
        noSummary: 'No hay resumen disponible.',
      },
      errors: {
        indexTitle: 'No se pudieron cargar clientes/proyectos.',
        indexDescription: 'Inténtalo de nuevo.',
        summaryTitle: 'No se pudo cargar el resumen.',
        summaryDescription: 'Inténtalo de nuevo.',
      },
      actions: {
        refresh: 'Actualizar',
        retry: 'Reintentar',
        upload: 'Iniciar subida',
      },
      qaTitle: 'Q&A del proyecto',
      qaDescription: 'Haz preguntas concretas para este cliente/proyecto. Las respuestas incluyen fuentes.',
    },
    jobs: {
      title: 'Jobs',
      description: 'La lista se actualiza automáticamente cada pocos segundos.',
      refresh: 'Actualizar',
      searchLabel: 'Buscar jobs',
      searchPlaceholder: 'Buscar por cliente, proyecto, estado o contenido…',
      searchLoading: 'La búsqueda se está cargando…',
      columns: {
        customer: 'Cliente',
        project: 'Proyecto',
        meeting: 'Reunión',
        status: 'Estado',
        source: 'Origen',
        engine: 'Motor',
        updated: 'Creado',
        actions: '',
        delete: 'Eliminar',
      },
      sourceLabel: 'Upload · Mistral',
      openButton: 'Abrir',
      failedLabel: 'Fallido',
      statusUnknown: 'Desconocido',
      deleteLabel: 'Eliminar',
      deleteConfirm: '¿Eliminar este job?',
      deleteError: 'No se pudo eliminar el job.',
      statusLabels: {
        APP_FACTORY_ACTIVE: 'App Factory activo',
        LIVE_ACTIVE: 'Live activo',
        LIVE: 'Live activo',
      },
      noMatches: 'Ningún job coincide con tu búsqueda.',
      empty: 'Todavía no hay uploads.',
      loadError: 'No se pudieron cargar los jobs.',
      steps: {
        UPLOADED: 'Upload',
        TRANSCRIBING: 'Transcripción',
        EXTRACTING: 'Extracción',
        READY: 'Listo',
      },
      engineLabels: {
        none: '—',
      },
      sourceLabels: {
        live: 'Live',
        upload: 'Upload',
        transcript: 'Transcript',
        appFactory: 'App Factory',
        unknown: 'Desconocido',
      },
    },
    dashboard: {
      welcome: {
        heading: 'Hola {{name}}',
        defaultName: 'tú',
        planLabel: 'Tu plan:',
        planFallback: 'Desconocido',
        priceFallback: '—',
        subtitle: 'Resumen de clientes, proyectos y jobs.',
        onboardingNote: 'Consejo: abre un job para filtrar temas y editar requisitos con registro de cambios.',
        actions: {
          live: 'Iniciar live',
          upload: 'Subir desde archivo',
          jobs: 'Lista de jobs',
          profile: 'Perfil y ajustes',
        },
      },
      panels: {
        title: 'Tus paneles clave',
        subtitle: 'Configura el espacio de trabajo según tus necesidades.',
        adminOnly: 'ADMIN ONLY',
        manageOpen: 'Gestionar paneles',
        manageClose: 'Cerrar selector de paneles',
        pinnedEmpty: 'Fija paneles para verlos aquí.',
        libraryEmpty: 'Ya fijaste todos los paneles.',
        pin: 'Fijar',
        unpin: 'Soltar',
        entries: {
          liveControl: {
            badge: 'Tiempo real',
            title: 'Sesión en vivo',
            description: 'Genera requisitos directamente durante la conversación y comparte la sala en vivo.',
            activeLabel: 'Sesiones activas',
            startButton: 'Iniciar sesión en vivo',
            shareButton: 'Compartir live',
            note: 'Todos ven la misma single source of truth.',
          },
          uploadFlow: {
            badge: 'Audio',
            title: 'Canal de subida',
            description: 'Pasos visuales para tus uploads de audio.',
            steps: ['Upload & validación', 'Transcripción', 'Extracción de requisitos', 'Export al formato deseado'],
            note: 'Si una etapa se queda bloqueada puedes reiniciarla por separado.',
          },
          exportHandoff: {
            badge: 'Formatos',
            title: 'Export & handoff',
            description: 'Entrega resultados en el artefacto exacto que espera tu cliente.',
            formats: ['Historias de usuario & import Jira', 'Pliego de requisitos', 'Plantillas DIN / ingeniería'],
            primaryButton: 'Elegir formato',
            secondaryButton: 'Descargar',
          },
          pilotProgram: {
            badge: 'Piloto',
            title: 'Programa piloto & campañas',
            description: 'Mantén alineados a los clientes piloto y los leads sociales.',
            activeLabel: 'Pilotos activos',
            note: 'El feedback llega directo a AnforderungsAI para iterar rápido.',
          },
          deviceReadiness: {
            badge: 'Dispositivos',
            title: 'Preparación de dispositivos',
            description: 'V1: desktop, tablets, móviles. V2: Apple Watch, más devices y plugin de Teams.',
            devices: ['Escritorio', 'Tablet', 'Móvil'],
            note: 'Planifica pronto relojes e integraciones para conservar la visibilidad live.',
          },
        },
      },
      insights: {
        title: 'Insights en vivo',
        subtitle: 'Estado de clientes, proyectos y sesiones activas.',
        planFallback: 'Plan',
        cards: {
          total: { label: 'Jobs totales', subline: 'incl. live y uploads' },
          running: { label: 'En ejecución', subline: 'con transcripción activa' },
          completed: { label: 'Finalizados', subline: 'listos para exportar' },
          customers: { label: 'Clientes y proyectos', subline: 'clientes / proyectos únicos' },
        },
      },
      portfolio: {
        title: 'Clientes y proyectos',
        subtitle: 'Vista rápida a partir de tus últimos jobs.',
        customersTitle: 'Clientes',
        projectsTitle: 'Proyectos',
        customerLine: '{{projects}} proyectos · {{jobs}} jobs',
        projectNamesLabel: 'Proyectos: {{names}}',
        requirementsTotalLabel: 'Requisitos totales: {{count}}',
        peopleLabel: 'Personas: {{names}}',
        projectStatusLabel: 'Estado',
        projectStatusActive: 'activo',
        projectStatusInactive: 'inactivo',
        jobLabel: 'Reunión {{date}}',
        requirementsCountLabel: '{{count}} requisitos',
        loadingLabel: '…',
        updatedLabel: 'Actualizado {{date}}',
        unknownCustomer: 'Sin cliente',
        empty: 'Aún no hay datos de clientes o proyectos.',
      },
      jobs: {
        title: 'Últimos jobs',
        countLoading: 'Cargando…',
        countEmpty: 'Sin jobs todavía',
        countLabel: 'Mostrando {{visible}} de {{total}} jobs',
        searchLabel: 'Buscar',
        searchPlaceholder: 'Cliente, proyecto o estado',
        filters: {
          all: 'Todos',
          running: 'En proceso',
          completed: 'Listos',
          error: 'Error',
        },
        table: {
          customer: 'Cliente',
          project: 'Proyecto',
          meeting: 'Reunión',
          status: 'Estado',
          updated: 'Creado',
          actions: '',
          open: 'Abrir',
          empty: 'Aún no hay jobs. Inicia un live o sube audio.',
          noMatches: 'Ningún job coincide con tu filtro.',
        },
        loading: 'Cargando…',
        loadError: 'No se pudieron cargar los jobs.',
        loadFailedLabel: 'Error al cargar',
      },
      statusLabels: {
        RUNNING: 'En ejecución',
        PROCESSING: 'Procesando',
        TRANSCRIBING: 'Transcribiendo',
        EXTRACTING: 'Analizando',
        COMPLETED: 'Terminado',
        READY: 'Listo',
        SUCCESS: 'Terminado',
        DONE: 'Terminado',
        FAILED: 'Error',
        ERROR: 'Error',
        CANCELED: 'Cancelado',
        PENDING: 'Pendiente',
      },
    },
    plans: {
      title: 'Planes y facturación',
      pillFallback: 'Plan desconocido',
      billing: {
        noInfo: 'Sin información disponible.',
        missing: 'Aún no hay método de pago registrado.',
        active: 'Facturación activa.',
        statusLabel: 'Estado',
        subscriptionLabel: 'Suscripción',
      },
      card: {
        current: 'Plan actual',
        checkout: 'Contratar',
        activate: 'Activar plan',
      },
      messages: {
        checkoutSuccess: 'Stripe Checkout completado. El plan se actualizará en breve.',
        checkoutCancel: 'Pago cancelado.',
        updateSuccess: 'Plan actualizado.',
        updateError: 'La actualización falló.',
        checkoutCreateError: 'No se pudo crear el Stripe Checkout.',
        checkoutFailed: 'Stripe Checkout falló.',
      },
    },
    profile: {
      title: 'Cuenta',
      tabs: {
        account: 'Información de la cuenta',
        personal: 'Datos personales',
        plan: 'Plan',
        export: 'Plantillas de exportación',
        billing: 'Facturación',
        releaseNotes: 'Notas de versión',
      },
      sectionTitles: {
        account: 'Información de la cuenta',
        plan: 'Plan actual',
        profile: 'Datos personales',
        password: 'Cambiar contraseña',
      },
      labels: {
        email: 'Correo electrónico',
        billing: 'Facturación actual:',
        requiredNote: '* Campos obligatorios',
        firstName: 'Nombre *',
        lastName: 'Apellido *',
        company: 'Empresa',
        phone: 'Teléfono *',
        vatNumber: 'IVA (opcional)',
        street: 'Calle y nº *',
        postalCode: 'CP *',
        city: 'Ciudad *',
        country: 'País *',
        profileButton: 'Guardar perfil',
        currentPassword: 'Contraseña actual',
        newPassword: 'Nueva contraseña',
        confirmPassword: 'Confirmar contraseña',
        passwordButton: 'Actualizar contraseña',
        deleteButton: 'Eliminar cuenta',
      },
      releaseNotes: {
        title: 'Notas de versión',
        subtitle: 'Todas las novedades de un vistazo.',
        empty: 'Aún no hay notas de versión.',
        loading: 'Cargando notas de versión…',
        loadError: 'No se pudieron cargar las notas de versión.',
        fallbackTitle: 'Versión',
      },
      plan: {
        unknown: 'Plan desconocido.',
        togglePicker: 'Cambiar plan',
        noAlternatives: 'No hay otros planes disponibles.',
        alternativesTitle: 'Planes disponibles',
        switchButton: 'Cambiar',
        currentBadge: 'Plan actual',
        pendingLabel: 'Cambio al plan "{{plan}}" pendiente.',
        notices: {
          pending: {
            title: 'Cambio pendiente',
            description: 'El cambio a "{{plan}}" aún no se completó. Finaliza el pago para activarlo.',
            action: 'Reanudar pago',
          },
          payment: {
            title: 'Requiere método de pago',
            description: 'El plan "{{plan}}" necesita un método de pago activo. Añádelo para habilitar los uploads.',
            action: 'Añadir método de pago',
          },
        },
        billingFallback: 'Tarjeta',
        billingMissing: 'No hay método de pago guardado.',
      },
      statuses: {
        planAlreadyActive: 'Este plan ya está activo.',
        unknownPlan: 'Plan desconocido.',
        openingCheckout: 'Abriendo Stripe Checkout…',
        updatingPlan: 'Actualizando plan…',
        redirectingStripe: 'Redirigiendo a Stripe…',
        planUpdated: 'Plan actualizado.',
        planFailed: 'El cambio de plan falló.',
        profileSaving: 'Guardando cambios…',
        profileSaved: 'Perfil guardado.',
        profileError: 'No se pudo guardar el perfil.',
        passwordMissingCurrent: 'La contraseña actual es obligatoria.',
        passwordTooShort: 'La nueva contraseña debe tener al menos 6 caracteres.',
        passwordMismatch: 'La nueva contraseña y su confirmación no coinciden.',
        passwordUpdating: 'Actualizando contraseña…',
        passwordUpdated: 'Contraseña actualizada.',
        passwordError: 'No se pudo actualizar la contraseña.',
        deleteConfirm: '¿Seguro que quieres eliminar tu cuenta? Esta acción no se puede deshacer.',
        deleteInProgress: 'Eliminando cuenta…',
        deleteSuccess: 'Cuenta eliminada. Se cerrará la sesión.',
        deleteFailed: 'No se pudo eliminar la cuenta.',
      },
    },
    auth: {
      titles: {
        login: 'Iniciar sesión',
        register: 'Crear cuenta',
      },
      sections: {
        profile: 'Datos personales',
      },
      descriptions: {
        login: 'Inicia sesión con tu correo para acceder a tus uploads.',
        register:
          'Crea una cuenta nueva. Las contraseñas se almacenan cifradas. Podrás pasar a un plan de pago en cualquier momento.',
      },
      requiredNote: '* Campos obligatorios',
      labels: {
        email: 'Correo *',
        password: 'Contraseña *',
        firstName: 'Nombre *',
        lastName: 'Apellido *',
        company: 'Empresa (opcional)',
        phone: 'Teléfono *',
        vatNumber: 'IVA (opcional)',
        street: 'Calle y nº *',
        postalCode: 'CP *',
        city: 'Ciudad *',
        country: 'País *',
      },
      placeholders: {
        company: 'Compañía',
        phone: '+34 600 123 456',
        vatNumber: 'ESB12345678',
        street: 'Calle Ejemplo 1',
        postalCode: '08000',
        city: 'Ciudad',
        country: 'España',
      },
      planSelector: {
        title: 'Elegir plan',
        subtitle: 'Selecciona el plan que mejor encaje. Lo podrás cambiar más tarde.',
        selectButton: 'Elegir plan',
        selectedButton: 'Seleccionado',
        currentLabel: 'Plan actual: {{plan}}',
        unknownPlan: 'Desconocido',
      },
      buttons: {
        submit: {
          login: 'Entrar',
          register: 'Registrarse',
        },
      },
      toggle: {
        login: {
          question: '¿Aún sin cuenta?',
          action: 'Crear cuenta',
        },
        register: {
          question: '¿Ya tienes cuenta?',
          action: 'Ir al login',
        },
      },
      forgotLink: '¿Olvidaste tu contraseña?',
      forgot: {
        title: 'Olvidé mi contraseña',
        subtitle: 'Introduce tu correo y te enviaremos un enlace de restablecimiento.',
        emailLabel: 'Correo electrónico',
        submit: 'Enviar enlace',
        sending: 'Enviando enlace…',
        sent: 'Si existe una cuenta, se ha enviado un correo de restablecimiento.',
        error: 'La solicitud falló.',
        backToLogin: 'Volver al login',
      },
      reset: {
        title: 'Restablecer contraseña',
        subtitle: 'Define una nueva contraseña para tu cuenta.',
        emailLabel: 'Correo electrónico',
        passwordLabel: 'Nueva contraseña',
        confirmLabel: 'Confirmar contraseña',
        submit: 'Guardar contraseña',
        saving: 'Guardando contraseña…',
        success: 'Contraseña actualizada. Ya puedes iniciar sesión.',
        error: 'No se pudo actualizar la contraseña.',
        missingToken: 'El enlace es inválido o falta.',
        backToRequest: 'Solicitar un nuevo enlace',
        backToLogin: 'Volver al login',
        passwordTooShort: 'La contraseña debe tener al menos 6 caracteres.',
        passwordMismatch: 'Las contraseñas no coinciden.',
      },
      statuses: {
        checkingCredentials: 'Verificando datos…',
        creatingAccount: 'Creando cuenta…',
        successRedirect: 'Éxito. Redirigiendo…',
        genericError: 'La acción falló.',
        redirectingCheckout: 'Redirigiendo al pago…',
        checkoutMissingUrl: 'Falta la URL de pago.',
        checkoutErrorPrefix: 'Error en el checkout:',
      },
    },
    admin: {
      title: 'Consola admin',
      description: 'Resumen de todas las cuentas registradas. Los cambios se aplican al instante (demo local).',
      toggle: { open: 'Nuevo usuario', close: 'Cancelar' },
      loading: 'Cargando usuarios…',
      empty: 'Todavía no hay usuarios.',
      addUser: { title: 'Crear usuario', submit: 'Crear usuario' },
      form: {
        firstName: 'Nombre',
        lastName: 'Apellido',
        email: 'Correo',
        password: 'Contraseña',
        company: 'Empresa',
        phone: 'Teléfono',
        vat: 'IVA (opcional)',
        street: 'Calle y nº',
        postalCode: 'CP',
        city: 'Ciudad',
        country: 'País',
        plan: 'Plan',
        role: 'Rol',
        uiVersion: 'Versión UI',
        newPassword: 'Nueva contraseña',
        confirmPassword: 'Confirmar contraseña',
      },
      placeholders: {
        company: 'Compañía',
        phone: '+34 600 123 456',
        vat: 'ESB12345678',
        street: 'Calle Ejemplo 1',
        postalCode: '08000',
        city: 'Ciudad',
        country: 'España',
      },
      table: {
        headers: {
          profile: 'Perfil',
          registered: 'Registrado',
          plan: 'Plan',
          role: 'Rol',
          uiVersion: 'Versión UI',
          stripe: 'Stripe',
          jobsCurrentMonth: 'Jobs (mes)',
          jobsTotal: 'Jobs (total)',
          jobs: 'Jobs',
          jobsCurrentMonthSub: 'por mes',
          jobsTotalSub: 'total',
          actions: 'Acciones',
        },
        profileFallback: '—',
        editButton: 'Editar',
      },
      stripe: { none: '—', open: 'abrir' },
      jobStats: {
        title: 'Jobs por mes',
        columns: { month: 'Mes', count: 'Jobs' },
        empty: 'No hay jobs registrados.',
      },
      plan: { unknown: 'Plan desconocido' },
      roles: { admin: 'Admin', user: 'Usuario' },
      uiVersion: {
        optionLabel: 'Versión {{version}}',
        badgeLabel: 'V{{version}}',
        invalid: 'Versión UI no válida.',
      },
      actions: { close: 'Cerrar', save: 'Guardar', cancel: 'Cancelar', delete: 'Eliminar cuenta' },
      modal: { title: 'Editar usuario' },
      statuses: {
        createRequired: 'El correo y la contraseña son obligatorios.',
        createSuccess: 'Usuario {{email}} creado.',
        createFailed: 'No se pudo crear.',
        passwordTooShort: 'La nueva contraseña debe tener al menos 6 caracteres.',
        passwordMismatch: 'La nueva contraseña y su confirmación no coinciden.',
        updateSaving: 'Guardando cambios…',
        updateSuccess: 'Cambios guardados para {{email}}.',
        updateFailed: 'No se pudo actualizar.',
        deleteConfirm: '¿Seguro que quieres borrar la cuenta?',
        deleteInProgress: 'Archivando cuenta…',
        deleteSuccess: 'Cuenta {{email}} archivada.',
        deleteFailed: 'No se pudo archivar.',
        loadFailed: 'No se pudieron cargar los usuarios.',
      },
    },
    live: {
      title: 'Transcripción en vivo',
      buttons: {
        start: 'Iniciar sesión en vivo',
        stop: 'Finalizar sesión',
        settingsOpen: 'Ajustes',
        settingsClose: 'Ocultar ajustes',
        transcriptShow: 'Mostrar transcripción en vivo',
        transcriptHide: 'Ocultar transcripción en vivo',
        share: 'Compartir enlace',
      },
      fields: {
        customerLabel: 'Cliente (opcional)',
        customerPlaceholder: 'Cliente',
        projectLabel: 'Proyecto (opcional)',
        projectPlaceholder: 'Proyecto',
        meetingLabel: 'Fecha de reunión (opcional)',
      },
      domainLabel: 'Dominio',
      domainOptions: [
        { value: 'enterprise-software', label: 'Software empresarial' },
        { value: 'bau', label: 'Construcción' },
        { value: 'maschinenbau', label: 'Ingeniería mecánica' },
        { value: 'produktentwicklung', label: 'Desarrollo de producto' },
        { value: 'sonstiges', label: 'Otro' },
      ],
      domainDefault: 'enterprise-software',
      info: {
        engineLabel: 'Motor',
        languageLabel: 'Idioma',
        languageUnknown: 'Sin detectar todavía.',
        languageManualSuffix: ' (manual)',
        statusReady: 'Listo. Esperando inicio.',
        extractionWarning:
          'No pudimos validar todas las exigencias. Mostramos los últimos resultados confirmados.',
        footer:
          'Al iniciar una sesión en vivo se transmiten en paralelo el micrófono y el audio del sistema para transcribirlos en tiempo real. Concede acceso del navegador a ambas fuentes.',
      },
      audio: {
        systemLabel: 'Sistema',
        micLabel: 'Micrófono',
        sourceSystem: 'Audio del sistema activo',
        sourceMic: 'Micrófono activo',
      },
      audioModes: [
        { code: 'mix', label: 'Sistema + micro', description: 'Capturar audio del sistema y micrófono a la vez' },
      ],
      requirements: {
        title: 'Requisitos detectados',
        empty: 'Aún no hay requisitos.',
        fallbackType: 'Otro',
        shareMissing: 'Todavía no hay requisitos disponibles.',
        shareCopied: '¡Enlace copiado!',
        shareCreateError: 'No se pudo crear el enlace.',
        shareClipboardError: 'No se pudo copiar. <a href="{{url}}" target="_blank">Abrir enlace</a>',
      },
      transcript: {
        empty: 'Aún no hay audio recibido.',
      },
      statuses: {
        settingsLockedLanguage: 'Sesión activa: idioma bloqueado.',
        settingsLockedEngine: 'Sesión activa: motor bloqueado.',
        settingsLockedAudio: 'Sesión activa: fuente de audio bloqueada.',
        settingsLockedDomain: 'Sesión activa: dominio bloqueado.',
        connecting: 'Conectando…',
        systemAudioUnsupported: 'Audio del sistema no disponible; grabando solo micrófono.',
        chunkBuffered: 'Fragmento incompleto: esperando más datos…',
        chunkSkipped: 'Fragmento omitido: incompleto.',
        sessionStreaming: 'Sesión activa: transmitiendo.',
        chunkSendFailed: 'Fallo al enviar. Intentando de nuevo…',
        waitingAudio: 'Sesión activa: esperando primeros datos de audio…',
        waitingAudioMic: 'Sesión activa: esperando primeros datos (solo micro).',
        stopError: 'No se pudo detener la sesión.',
        startError: 'No se pudo iniciar la sesión.',
      },
      appFactory: {
        title: 'App Factory',
        description: 'Conduce entrevistas estructuradas desde la vista en vivo.',
        sessionSelectLabel: 'Seleccionar sesión',
        sessionSelectPlaceholder: 'Elige una sesión…',
        sessionEmpty: 'Aún no hay sesiones de App Factory.',
      buttons: {
        refresh: 'Actualizar',
        newSession: 'Nueva sesión',
        analyze: 'Analizar progreso',
        sync: 'Sincronizar requisitos',
        open: 'Abrir sesión',
      },
      sessionLabel: 'Sesión',
      customerLabelShort: 'Cliente',
      projectLabelShort: 'Proyecto',
      autoSyncLabel: 'Sincronizar requisitos automáticamente',
        requirementsTitle: 'Requisitos en vivo',
        requirementsHint: 'Selecciona una sesión para sincronizar.',
        requirementsSummary: '{{count}} requisitos listos.',
        guidelineEmpty: 'No hay guía cargada.',
        status: {
          selectSession: 'Selecciona una sesión.',
          loadingSessions: 'Cargando sesiones…',
          loadingSession: 'Cargando guía…',
          syncEmpty: 'Aún no hay requisitos.',
          syncing: 'Sincronizando requisitos…',
          syncSuccess: 'Requisitos sincronizados.',
          analyzing: 'Analizando…',
          updatedAt: 'Actualizado a las {{time}}',
        },
        errors: {
          load: 'No se pudieron cargar las sesiones.',
          create: 'No se pudo crear la sesión.',
          analyze: 'No se pudo analizar el progreso.',
          sync: 'No se pudieron sincronizar los requisitos.',
          session: 'No se pudo cargar la sesión.',
          init: 'App Factory no pudo inicializarse.',
        },
        prompts: {
          customer: 'Opcional: introduce el ID del cliente',
          project: 'Opcional: introduce el ID del proyecto',
        },
        creating: 'Creando sesión…',
      },
    },
    liveShare: {
      title: 'Requisitos compartidos',
      loadError: 'No se pudo leer el enlace. Solicítalo de nuevo.',
      sessionLabel: 'Sesión del {{date}}',
      sharedBy: {
        prefix: 'Compartido por',
        unknown: 'desconocido',
        companySeparator: ' – ',
      },
      buttons: {
        csv: 'Descargar CSV',
        md: 'Descargar Markdown',
      },
      empty: 'No se compartieron requisitos.',
      note: 'Nota: esta vista muestra una instantánea de la sesión en vivo. Las actualizaciones en tiempo real llegarán con el nuevo backend.',
      card: {
        fallbackType: 'Otro',
      },
      download: {
        csvHeaders: {
          type: 'Tipo',
          text: 'Texto',
        },
        markdown: {
          title: 'Requisitos detectados',
          sessionLabel: 'Sesión',
        },
      },
    },
    conversation: {
      title: 'Conversación',
      loading: 'Cargando resultados…',
      backToJobs: 'Volver a jobs',
      backLabel: 'Volver',
      header: {
        status: 'Estado',
        conversationId: 'ID de conversación',
        customer: 'Cliente',
        project: 'Proyecto',
        meeting: 'Reunión',
        created: 'Creado',
        updated: 'Actualizado',
      },
      progress: {
        label: 'Estado',
        phases: {
          uploaded: 'Subido',
          processing: 'Procesando',
          transcribing: 'Transcribiendo',
          extracting: 'Extrayendo eventos',
          live: 'Sesión en vivo activa',
          finalizing: 'Finalizando',
          finalized: 'Completado',
          failed: 'Fallido',
          unknown: 'Desconocido',
        },
      },
      delete: {
        button: 'Eliminar job',
        confirm: '¿Eliminar este job? Se eliminarán requisitos, transcripts y archivos.',
        inProgress: 'Eliminando…',
        success: 'Job eliminado.',
        error: 'No se pudo eliminar el job.',
      },
      metaEdit: {
        button: 'Editar',
        save: 'Guardar',
        cancel: 'Cancelar',
        inProgress: 'Guardando…',
        error: 'La actualización falló.',
      },
      filters: {
        topics: 'Temas',
        topicsAll: 'Todos los temas',
        noTopics: 'No hay temas disponibles.',
        contentLabel: 'Mostrar',
        contentAll: 'Todo',
        priorityLabel: 'Prioridad',
        priorityAll: 'Todas las prioridades',
        categoryLabel: 'Categoría',
        categoryAll: 'Todas las categorías',
        searchLabel: 'Buscar',
        searchPlaceholder: 'Texto, ID o palabra clave',
        clear: 'Restablecer filtros',
        countLabel: '{{count}} resultados',
        noResults: 'Ningún elemento coincide con tus filtros.',
        emptyAll: 'Aún no hay requisitos ni user stories.',
        priorityOptions: {
          high: 'Alta',
          medium: 'Media',
          low: 'Baja',
          must: 'Must',
          should: 'Should',
          could: 'Could',
          wont: "Won't",
        },
        topicLabels: {
          functional: 'Funcional',
          non_functional: 'No funcional',
          business: 'Business',
          knowledge_management: 'Gestión del conocimiento',
          system_integration: 'Integración de sistemas',
          lifecycle_tracking: 'Seguimiento del ciclo de vida',
          onboarding_enablement: 'Habilitación de onboarding',
          global_enablement: 'Habilitación global',
          data_governance: 'Gobernanza de datos',
          analytics_automation: 'Automatización analítica',
          service_quality: 'Calidad de servicio',
          other: 'Otro',
        },
      },
      sections: {
        requirements: 'Requisitos',
        userStories: 'Historias de usuario',
        deferredDecisions: 'Decisiones aplazadas',
      },
      summary: {
        title: 'Executive Summary',
        download: 'Descargar resumen',
        downloadDescription: 'Executive Summary (Markdown)',
        collapse: 'Contraer',
        expand: 'Expandir',
        overview: 'Resumen',
        keyRequirements: 'Requisitos clave',
        openDecisions: 'Decisiones abiertas',
        risks: 'Riesgos',
        nextSteps: 'Próximos pasos',
        downloadSuccess: 'Descarga del Executive Summary iniciada.',
        downloadError: 'No se pudo descargar el Executive Summary.',
      },
      exports: {
        csv: 'Exportar CSV',
        md: 'Exportar Markdown',
      },
      requirements: {
        empty: 'Todavía no hay requisitos.',
        rationale: 'Justificación',
        source: 'Fuente',
        speaker: 'Interlocutor',
        tags: 'Etiquetas',
      },
      edit: {
        editButton: 'Editar',
        copyButton: 'Copiar',
        copySuccess: 'Copiado.',
        copyError: 'Error al copiar.',
        saveButton: 'Guardar',
        cancelButton: 'Cancelar',
        priorityUnset: 'Sin prioridad',
        fields: {
          text: 'Texto',
          story: 'User story',
          role: 'Rol',
          goal: 'Objetivo',
          benefit: 'Beneficio',
          acceptance: 'Criterios de aceptación',
          category: 'Categoría',
          priority: 'Prioridad',
          topics: 'Temas',
        },
        topicsPlaceholder: 'p. ej., Seguridad, Reporting',
      },
      userStories: {
        empty: 'Todavía no hay historias.',
        role: 'Rol',
        goal: 'Objetivo',
        benefit: 'Beneficio',
        acceptance: 'Criterios de aceptación',
        priorityFallback: 'Media',
      },
      log: {
        title: 'Registro de cambios',
        empty: 'Sin cambios todavía.',
        note: 'Los cambios se guardan en la base de datos.',
        emptyValue: '—',
      },
    },
    qaChat: {
      title: 'Chat de Q&A con fuentes',
      description: 'Haz preguntas sobre requisitos y transcripciones. Las respuestas incluyen evidencia rastreable.',
      requirementsTitle: 'Q&A entre proyectos',
      requirementsDescription: 'Los filtros actuales se usan como contexto (cliente, proyecto, periodo).',
      conversationTitle: 'Q&A de la conversación',
      conversationDescription: 'Pregunta sobre esta conversación con fuentes, interlocutor y tiempo.',
      roles: {
        user: 'Tú',
        assistant: 'Asistente',
      },
      state: {
        loading: 'Cargando chat…',
        empty: 'Aún no hay mensajes. Haz tu primera pregunta.',
      },
      input: {
        placeholder: 'Ejemplo: ¿Qué color debe tener la casa?',
      },
      actions: {
        send: 'Enviar',
        newThread: 'Nuevo hilo',
        deleteThread: 'Eliminar',
        includeGeneralAnswer: '+ Conocimiento general (Prueba)',
        searchGlobal: 'Buscar globalmente',
      },
      labels: {
        generalAnswer: 'Conocimiento general (Prueba)',
      },
      feedback: {
        threadDeleted: 'Hilo eliminado.',
      },
      citations: {
        title: 'Fuentes',
        unknownSpeaker: 'Interlocutor desconocido',
        unknownCustomer: 'Cliente desconocido',
        unknownProject: 'Proyecto desconocido',
      },
      errors: {
        default: 'La solicitud falló.',
        send: 'No se pudo enviar el mensaje.',
        loadThread: 'No se pudo cargar el hilo.',
        createThread: 'No se pudo crear el hilo.',
        deleteThread: 'No se pudo eliminar el hilo.',
        loadThreads: 'No se pudieron cargar los hilos.',
      },
    },
    imprint: {
      title: 'Aviso legal',
      intro: 'Este aviso legal aplica al servicio AnforderungsAI disponible en https://anforderungsai.de.',
      tabs: {
        imprint: 'Aviso legal',
        agb: 'Términos',
        privacy: 'Privacidad',
      },
      provider: {
        heading: 'Proveedor',
        nameLabel: 'Nombre',
        name: IMPRINT_DETAILS.name,
        addressLabel: 'Dirección',
        addressLines: [IMPRINT_DETAILS.street, IMPRINT_DETAILS.postalCodeCity, IMPRINT_DETAILS.country],
        registerLabel: 'Registro mercantil',
        registerNote: 'Sin inscripción en el registro mercantil.',
      },
      contact: {
        heading: 'Contacto',
        emailLabel: 'Correo electrónico',
        email: IMPRINT_DETAILS.email,
        phoneLabel: 'Teléfono',
        phone: 'No publicado; contacta por correo electrónico.',
      },
      vat: {
        heading: 'ID fiscal',
        text: `NIF-IVA (art. 27a UStG): ${IMPRINT_DETAILS.taxId}`,
      },
      supervision: {
        heading: 'Autoridad de supervisión',
        text: 'No aplica ninguna autoridad de supervisión específica.',
      },
      profession: {
        heading: 'Regulación profesional',
        text: 'No existen profesiones reguladas ni obligaciones profesionales especiales.',
      },
      dispute: {
        heading: 'Resolución de litigios / Plataforma ODR',
        text: 'La Comisión Europea ofrece una plataforma de resolución de litigios en línea (ODR):',
        platformLabel: 'https://ec.europa.eu/consumers/odr',
        participation: 'No estamos obligados ni dispuestos a participar en procedimientos ante juntas arbitrales de consumo.',
      },
      editorial: {
        heading: 'Responsable según § 18 (2) MStV',
        text: `${IMPRINT_DETAILS.name}, misma dirección.`,
      },
      notes: {
        lastUpdated: 'Actualizado: enero de 2025',
      },
    },
  },
};

const translatePath = (language, path) => {
  const content = LANG_CONTENT[language] ?? LANG_CONTENT.de;
  return path.split('.').reduce((acc, segment) => (acc ? acc[segment] : undefined), content);
};

let currentLanguage = loadStoredLanguage();

if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('lang', currentLanguage);
}

export const getLanguage = () => currentLanguage;

export const setLanguage = (language) => {
  const next = normalizeLanguage(language);
  if (next === currentLanguage) return;
  currentLanguage = next;
  saveLanguage(next);
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', next);
  }
  window.dispatchEvent(new CustomEvent('anforderungsai:language-changed', { detail: next }));
};

export const translate = (path, language = currentLanguage) =>
  translatePath(language, path) ?? translatePath('de', path) ?? path;

const getContent = (language = currentLanguage) => LANG_CONTENT[language] ?? LANG_CONTENT.de;

export const getSidebarCopy = (language) => getContent(language).sidebar;
export const getNavCopy = (language) => getContent(language).nav;
export const getHomeCopy = (language) => getContent(language).home;
export const getUploadCopy = (language) => getContent(language).upload;
export const getNewRequirementsCopy = (language) => getContent(language).newRequirements;
export const getRequirementsListCopy = (language) => getContent(language).requirementsList;
export const getCustomersProjectsCopy = (language) => getContent(language).customersProjects;
export const getJobsCopy = (language) => getContent(language).jobs;
export const getDashboardCopy = (language) => getContent(language).dashboard;
export const getReleaseNotesCopy = (language) => getContent(language).releaseNotes;
export const getPlansCopy = (language) => getContent(language).plans;
export const getProfileCopy = (language) => getContent(language).profile;
export const getAuthCopy = (language) => getContent(language).auth;
export const getAdminCopy = (language) => getContent(language).admin;
export const getLiveCopy = (language) => getContent(language).live;
export const getLiveShareCopy = (language) => getContent(language).liveShare;
export const getConversationCopy = (language) => getContent(language).conversation;
export const getQaChatCopy = (language) => getContent(language).qaChat;
export const getImprintCopy = (language) => getContent(language).imprint;
export const getCommonCopy = (language) => getContent(language).common;
export const getBillingCopy = (language) => getContent(language).billing ?? {
  title: 'Abrechnung',
  tabs: {
    overview: 'Übersicht',
    invoices: 'Rechnungen',
    paymentMethod: 'Zahlungsmethode',
  },
  overview: {
    title: 'Abrechnungsübersicht',
    currentPlan: 'Aktueller Tarif',
    status: 'Status',
    subscriptionId: 'Subscription ID',
    paymentMethod: 'Zahlungsmethode',
    noPaymentMethod: 'Keine Zahlungsmethode hinterlegt',
    changePlan: 'Tarif wechseln',
  },
  preference: {
    title: 'Rechnungsoptionen (Pay per Use)',
    description: 'Wähle, wann du eine Rechnung erhältst. Abbuchung erfolgt immer direkt pro Nutzung.',
    options: {
      perUse: {
        title: 'Rechnung pro Nutzung (Standard)',
        description: 'Für jeden Job wird eine einzelne Rechnung erstellt.',
      },
      monthly: {
        title: 'Monatliche Sammelrechnung',
        description: 'Am Monatsende erhältst du eine Sammelrechnung. Abbuchung erfolgt trotzdem sofort pro Nutzung.',
      },
    },
    saved: 'Abrechnungsoption gespeichert.',
    saveError: 'Abrechnungsoption konnte nicht gespeichert werden.',
  },
  invoices: {
    title: 'Rechnungen',
    loading: 'Rechnungen werden geladen...',
    noInvoices: 'Noch keine Rechnungen vorhanden.',
    loadError: 'Rechnungen konnten nicht geladen werden.',
    table: {
      date: 'Datum',
      description: 'Beschreibung',
      amount: 'Betrag',
      status: 'Status',
      actions: 'Aktionen',
    },
  },
  paymentMethod: {
    title: 'Zahlungsmethode',
    noMethod: 'Noch keine Zahlungsmethode hinterlegt.',
    expires: 'Läuft ab',
    updateButton: 'Zahlungsmethode ändern',
    addButton: 'Zahlungsmethode hinzufügen',
    updateTitle: 'Zahlungsmethode aktualisieren',
    updateDescription: 'Gib deine neue Kreditkartendaten ein. Deine bestehende Zahlungsmethode wird ersetzt.',
  },
};
